import countries from 'i18n-iso-countries'
import countriesJSON from 'i18n-iso-countries/langs/en.json'

import { zodEnJSON as zodLanguageJSON } from '@/utils/zod/zod-i18n'

import { compensationEn } from './compensation/en'
import { network } from './network/en'
import { selfServiceEn } from './selfService/en'
import statesJSON from './states/en.json'

countries.registerLocale(countriesJSON)

export const en = {
    zod: {
        errors: {
            too_small: zodLanguageJSON.errors.too_small,
            too_big: zodLanguageJSON.errors.too_big,
        },
        custom: {
            eventServiceSelection: {
                required: 'Select at least one optional service.',
                single: 'Select no more than one optional service.',
            },
            required: 'Value required',
            invalidCountry: 'Invalid country',
            invalidValue: 'Invalid value',
            invalidZip: 'Invalid zip',
            invalidState: 'Invalid state',
            invalidTime: 'Invalid time',
            invalidDateOnly: 'Invalid date only (utc hours must be zero)',
            invalidTimeRange: 'Invalid time range',
            invalidDateRange: 'Invalid date range',
            invalidDateTimeRange: 'Invalid date time range',
            invalidMinDate: 'Date is before the minimum allowed date',
            invalidMaxDate: 'Date is after the maximum allowed date',
            invalidRRule: 'Invalid reccuring rule string',
            invalidTaxID: 'Invalid tax-id',
            invalidVATID: 'Invalid VAT-number',
            invalidCreditorIdentifier: 'Invalid creditor identifier',
            invalidBIC: 'Invalid BIC',
            invalidIBAN: 'Invalid IBAN',
            invalidSepaMandateId: 'Invalid sepa mandate-id',
            invalidObjectId: 'Invalid ObjectId',
            invalidPhone: 'Invalid phone number',
            invalidEmail: 'Invalid Email address',
            invalidOption: 'Invalid option',
            invalidMessageAttachments: 'Use one attachment category per message. Send voice notes without a caption.',
            invalidFileType: 'Invalid file type',
            invalidFileSize: 'File exceeds the maximum allowed size',
            chatPollDuplicateOption: 'Survey options must have unique IDs and text.',
            chatPollDeadlineRequired: 'Set a closing time when results are hidden until closing.',
            chatContentRequired: 'Write some text or add at least one attachment. A title alone is not enough.',
            rawHtml: 'Use the editor formatting instead of HTML.',
            unsafeLink: 'Use a complete http, https, mailto or tel link.',
            imageReference: 'Insert an image from this post’s attachments.',
            chatDuplicateAttachment: 'Each attachment must have its own identifier.',
            chatAttachmentsTooLarge: 'The attachments together exceed 2 GB.',
            invalidCalculatedValue: 'Calculated value could not be evaluated',
            invalidReference: 'Invalid or not existent reference',
            invalidArray: 'Invalid array',
            resourceRequired: 'A file has to be selected',
            fromBeforeTo: 'From must be before to',
            minBeforeMax: 'Min must be less than max',
            divisionByZero: 'Division by zero is not allowed',
            rruleMustEnd: 'The given recurring rule must have an ending',
            duplicate: 'Duplicate value',
            cannotChangeSingleMultiple:
                'The repeating/non repeating setting cannot be changed after the group has been saved',
            invalidRegex: 'Invalid regular expression',
            donationProposalOutcomeNotAllowed:
                'Donation proposal outcome is only allowed for cash transaction rules matching revenues',
            invalidNumber: 'Invalid number',
            invalidCron: 'Invalid cron expression',
            contactRuleNotificationMessageOrDetailsRequired: 'Message or details is required',
            onlinePaymentProviderRequired: 'Select at least one online payment provider',
            iterateModeBaseIntervalMustBeLessThanIntervals:
                'Iterate mode cannot be used with a base interval that is greater than any of the flexible intervals',
        },
    },
    translation: {
        websiteViewCss: {
            atRule: 'Unsupported CSS rule: @{{name}}. Use @media, @supports or @container.',
            declaration: 'Declarations must belong to a CSS selector.',
            external: 'External resources and executable expressions are not supported.',
            nesting: 'Use complete selectors instead of nested rules or &.',
            selector: 'Selectors must start with an element, class or attribute.',
        },
        actions: {
            saveChanges: 'Save changes',
        },
        contactAccessCriteria: {
            matchingHint:
                'Within each category (contact types, segments, departments or tags), one selected value must match. All categories with selections must match. Empty categories impose no restriction.',
            contactTypes: 'Contact types',
            segments: 'Segments',
            departments: 'Departments',
            tags: 'Tags',
            all: 'All',
            invalidDepartment: 'A selected department is no longer available.',
            invalidSegment: 'A selected segment is no longer available.',
        },
        selfService: selfServiceEn,
        compensation: compensationEn,
        network,
        reports: {
            tabs: { types: 'Types' },
            list: {
                title: 'List',
                table: {
                    date: 'Date',
                    user: 'User',
                    organization: 'Organization',
                    category: 'Category',
                    type: 'Type',
                    description: 'Description',
                    note: 'Note',
                    createdAt: 'Created at',
                },
                editor: {
                    spentMinutes: 'Spent minutes',
                    billableMinutes: 'Billable minutes',
                },
            },
            types: {
                name: 'Name',
                price: 'Net hourly price',
                descriptionRequired: 'Description required',
            },
        },
        ...statesJSON,
        language: {
            en: 'English',
            de: 'German',
        },
        timeUnit: {
            millisecond: 'Millisecond',
            milliseconds: 'Milliseconds',
            second: 'Second',
            seconds: 'Seconds',
            minute: 'Minute',
            minutes: 'Minutes',
            hour: 'Hour',
            hours: 'Hours',
            day: 'Day',
            days: 'Days',
            week: 'Woche',
            weeks: 'Wochen',
            month: 'Month',
            months: 'Months',
            quarter: 'Quarter',
            quarters: 'Quarters',
            year: 'Year',
            years: 'Years',
            halfYear: 'Half year',
            halfYears: 'Half years',
            halfYearAbbreviated: 'H-Year',
        },
        rruleIds: {
            repeats: 'Repeats',
            eventCount: 'Repeats {{count, number}} times',
            eventUntil: 'Repeats until {{date}}',
            every: 'every',
            until: 'until',
            for: 'for',
            in: 'in',
            the: 'the',
            and: 'and',
            or: 'or',
            on: 'on',
            'on the': 'on the',
            last: 'last',
            st: 'st',
            nd: 'nd',
            rd: 'rd',
            th: 'th',
            time: 'time',
            times: 'times',
            hour: 'hour',
            hours: 'hours',
            minute: 'minute',
            minutes: 'minutes',
            weekday: 'weekday',
            weekdays: 'weekdays',
            day: 'day',
            days: 'days',
            week: 'week',
            weeks: 'weeks',
            month: 'month',
            months: 'months',
            year: 'year',
            years: 'years',
        },
        libCommon: {
            finance: {
                accountingStatementEditorIssue: {
                    emptyStatementName: 'The report name is required.',
                    emptyStatementTitle: 'The report title is required.',
                    emptyTemplate: 'Add at least one line to the income statement.',
                    emptyLineLabel: 'Each line needs a label.',
                    duplicateSiblingLabel: 'The label "{{label}}" is duplicated within the same section.',
                    emptyExpressionReference: 'Each formula row needs a referenced line.',
                    missingExpressionReference: 'The line "{{reference}}" cannot be referenced from here.',
                    missingAccount: 'Account {{account}} does not exist in the accounting plan.',
                    invalidAccountRange: 'The range {{from}} - {{to}} is invalid.',
                    emptyAccountRange: 'The range {{from}} - {{to}} does not contain any existing account.',
                },
            },
            work: {
                todo: {
                    errors: {
                        todoNotFound: 'Todo not found',
                        assignmentNotFound: 'Assignment not found',
                        transactionRequired: 'This operation must run within a database transaction',
                        parentMustBeOpen: 'Reopen the parent Todo before adding or reopening a sub-todo',
                        maximumChildren: 'This Todo already has the maximum number of sub-todos',
                        invalidParent: 'A Todo cannot be its own parent or be nested below more than one level',
                        parentTopicMismatch: 'A Todo and its parent must belong to the same topic',
                        parentProjectMismatch: 'A Todo and its parent must belong to the same project',
                        parentContextMismatch:
                            'A Todo and its parent must be linked to the same team, event, or resource',
                        assigneesIncomplete: 'All assignees must complete their work first',
                        completionStateIncomplete: 'The previous Todo status is missing',
                        customFieldsUnavailable: 'This topic does not define custom fields',
                        alreadyCompleted: 'The Todo is already completed',
                        itemChanged: 'This Todo changed. Refresh it before trying again.',
                        editLocked: 'Reopen this Todo or withdraw its review before editing it.',
                        reviewerUnavailable:
                            'The reviewer needs administration access and permission to read Todos. Choose an eligible reviewer.',
                        alreadyWaitingForReview: 'The Todo is already waiting for review',
                        completeSubtodosFirst: 'Complete all sub-todos first',
                        completedStatusMissing: 'This workflow has no active completed status',
                        chooseReviewer: 'Choose a reviewer before completing this Todo',
                        parentProgressInconsistent: 'Parent Todo progress is inconsistent',
                        unknownUser: 'Unknown organization user',
                        unknownContact: 'Unknown organization contact',
                        invalidTeamAssignees: 'Team todos can only be assigned to contacts belonging to that team.',
                        duplicatePerson: 'The same person cannot be assigned twice: {{duplicates}}',
                        duplicatePersonUser: '{{name}} (user)',
                        duplicatePersonContact: '{{name}} (contact)',
                        unknownTeam: 'Unknown team',
                        unknownEvent: 'Unknown event',
                        unknownResource: 'Unknown resource',
                        unknownProject: 'Unknown or archived project',
                        projectRequired: 'Select a project when the todo has no team, event, or resource',
                        projectWithContext: 'Only todos without a linked team, event, or resource can have a project',
                        unknownWorkflow: 'Unknown or archived Todo workflow',
                        statusTransitionNotAllowed: 'This status change is not allowed by the workflow',
                    },
                },
            },
        },
        entities: {
            common: {
                clientAppType: {
                    admin: 'Admin',
                    'connect-web': 'Community-App Web',
                    'connect-ios': 'Community-App iOS',
                    'connect-android': 'Community-App Android',
                    'portal-web': 'Portal Web',
                    'portal-ios': 'Portal iOS',
                    'portal-android': 'Portal Android',
                    embeddable: 'Embeddable',
                    root: 'Root',
                },
                mandate: {
                    entityName_one: 'Mandate',
                    entityName_other: 'Mandates',
                },
                address: {
                    address: {
                        groupName: 'Address',
                        country: 'Country',
                        state: 'State',
                        zip: 'Zip',
                        city: 'City',
                        addressLine: 'Street address',
                        details1: 'Recipient Details 1',
                        details2: 'Recipient Details 2',
                    },
                },
                recipientType: {
                    business: 'Business',
                    person: 'Individual',
                },
            },
            notification: {
                category: {
                    teams: 'Teams',
                    selfService: 'Self-Service',
                    network: 'Network',
                    'community.reply': 'Community reply',
                    'feedback.comment': 'Feedback comment',
                    'feedback.tickets': 'Feedback tickets',
                    export: 'Export',
                    job: 'Job',
                    review: 'Reviews',
                    'questionnaire.submission': 'Questionnaire submission',
                    'contact.resubmission': 'Contact resubmission',
                    'contact.rule': 'Contact change',
                    'conversation.inbox': 'Inbox',
                    todo: 'Todos',
                    booking: 'Bookings',
                    compensation: 'Compensation claims',
                },
            },
            work: {
                todoProject: {
                    groupName: 'Project',
                    name: 'Name',
                    color: 'Color',
                    colors: {
                        slate: 'Slate',
                        gray: 'Gray',
                        zinc: 'Zinc',
                        neutral: 'Neutral',
                        stone: 'Stone',
                        red: 'Red',
                        orange: 'Orange',
                        amber: 'Amber',
                        yellow: 'Yellow',
                        lime: 'Lime',
                        green: 'Green',
                        emerald: 'Emerald',
                        teal: 'Teal',
                        cyan: 'Cyan',
                        sky: 'Sky',
                        blue: 'Blue',
                        indigo: 'Indigo',
                        violet: 'Violet',
                        purple: 'Purple',
                        fuchsia: 'Fuchsia',
                        pink: 'Pink',
                        rose: 'Rose',
                    },
                    description: 'Description',
                    openCount: 'Open todos',
                    closedCount: 'Closed todos',
                    createdAt: 'Created at',
                    updatedAt: 'Updated at',
                    archivedAt: 'Archived at',
                },
                todoTopic: {
                    groupName: 'Topic',
                    name: 'Name',
                    description: 'Description',
                    openCount: 'Open todos',
                    closedCount: 'Closed todos',
                    createdAt: 'Created at',
                    updatedAt: 'Updated at',
                    archivedAt: 'Archived at',
                },
                todoItem: {
                    item: {
                        groupName: 'Todo',
                        title: 'Title',
                        description: 'Description',
                        createdAt: 'Created at',
                        updatedAt: 'Updated at',
                    },
                    workflow: {
                        project: 'Project',
                        groupName: 'Workflow',
                        topic: 'Topic',
                        type: 'Type',
                        status: 'Status',
                        statusRole: 'Status role',
                        completion: 'Completion',
                        completionStates: {
                            open: 'Open',
                            completed: 'Done',
                        },
                        statusRoles: {
                            regular: 'Regular',
                            review: 'Review',
                            completed: 'Completed',
                        },
                    },
                    people: {
                        groupName: 'Assignees and review',
                        assignees: 'Assignees',
                        assignedUsers: 'Assigned users',
                        assignedContacts: 'Assigned contacts',
                        reviewer: 'Reviewer',
                        reviewDecision: 'Review status',
                        reviewDecisions: {
                            none: 'No review',
                            pending: 'Pending',
                            accepted: 'Accepted',
                            rejected: 'Rejected',
                            withdrawn: 'Withdrawn',
                        },
                    },
                    context: {
                        groupName: 'Related to',
                        type: 'Link type',
                        name: 'Related to',
                        types: {
                            team: 'Team',
                            event: 'Event',
                            resource: 'Resource',
                        },
                    },
                    schedule: {
                        groupName: 'Schedule',
                        dueDate: 'Due date',
                        workCompletedAt: 'Submitted at',
                        completedAt: 'Completed at',
                    },
                    activity: {
                        groupName: 'Activity',
                        tags: 'Tags',
                        attachmentCount: 'Number of attachments',
                        attachmentCountInfo:
                            'Use zero for todos without attachments and greater than zero for todos with attachments.',
                        commentsCount: 'Number of comments',
                        childrenCount: 'Number of subtodos',
                        childrenCompletedCount: 'Completed subtodos',
                    },
                },
            },
            organization: {
                organization: {
                    common: {
                        groupName: 'Common',
                        shortId: 'Short id',
                        name: 'Name',
                        website: 'Website',
                        foundingYear: 'Founding year',
                        memberCount: 'Members',
                        childrenMemberCount: 'Child members',
                        userCount: 'Users',
                        createdAt: 'Created at',
                        language: 'Language',
                        address: 'Address',
                    },
                    classification: {
                        groupName: 'Classification',
                        type: 'Type',
                        area: 'Area',
                        sports: 'Sports',
                    },
                    hierarchy: {
                        groupName: 'Hierarchy',
                        parentOrganizations: 'Parent organizations',
                        childrenCount: 'Children count',
                    },
                    subscriptions: {
                        groupName: 'Subscriptions',
                        product: {
                            walletPasses: 'Wallet passes',
                            outgoingMessagesArchive: 'Outgoing messages archive',
                            fileStorage: 'File storage',
                        },
                    },
                    billing: {
                        groupName: 'Billing',
                        groupNameShort: 'Billing',
                        stripeCustomerId: 'Stripe customer ID',
                        products: 'Products',
                        productOptions: {
                            main: 'Members',
                            walletPasses: 'Wallet passes',
                            outgoingMessagesArchive: 'Outgoing messages archive',
                            fileStorage: 'File storage',
                        },
                        revenue: {
                            standalonePayments: 'Standalone payments',
                            lifetimeValue: 'Lifetime value',
                            currentMrr: 'Current MRR',
                            commissions: {
                                stripe: 'Stripe commissions',
                                paypal: 'PayPal commissions',
                            },
                        },
                        subscription: {
                            groupName: 'Subscription',
                            groupNameShort: 'Sub',
                            id: 'Subscription ID',
                            status: 'Status',
                            isCustom: 'Custom subscription',
                            startDate: 'Start date',
                            trialEndDate: 'Trial end date',
                            cancelDate: 'Cancellation date',
                            cancelReason: {
                                reason: 'Cancellation reason',
                                comment: 'Cancellation comment',
                            },
                            cancelBehaviour: 'Cancellation behavior',
                            statusOptions: {
                                trialing: 'Trial',
                                active: 'Active',
                                pastDue: 'Past due',
                                unpaid: 'Unpaid',
                                willCancel: 'Cancellation scheduled',
                                canceled: 'Canceled',
                            },
                            cancelReasonOptions: {
                                other: 'Other',
                                customer_service: 'Customer service',
                                low_quality: 'Low quality',
                                missing_features: 'Missing features',
                                switched_service: 'Switched service',
                                too_complex: 'Too complex',
                                too_expensive: 'Too expensive',
                                unused: 'Unused',
                                trial_expired: 'Trial expired',
                                first_payment_failed: 'First payment failed',
                            },
                            cancelBehaviourOptions: {
                                lockOrganization: 'Lock organization',
                                deleteOrganization: 'Delete organization',
                            },
                        },
                        accessLock: {
                            groupName: 'Access lock',
                            groupNameShort: 'Lock',
                            isLocked: 'Locked',
                            adminLockedAt: 'Admin access locked at',
                            publicLockedAt: 'Public access locked at',
                            deletionAt: 'Organization deletion date',
                        },
                    },
                    integrations: {
                        groupName: 'Integrations',
                        expressaTenant: 'Expressa tenant',
                        stripeAccount: 'Stripe account',
                        paypalAccount: 'PayPal account',
                        eventim: 'Eventim',
                        tixxId: 'Tixx ID',
                    },
                    internal: {
                        groupName: 'Internal',
                        credit: 'Credit',
                        verified: 'Verified',
                        onlineLetterSending: 'Online letter sending',
                        newCRMMode: 'New CRM mode',
                        migrationStatus: 'Migration status',
                    },
                },
            },
            propertyFilter: {
                comparator: {
                    string: {
                        equals: 'Equals',
                        notEquals: 'Does not equal',
                        contains: 'Contains',
                        notContains: 'Not contains',
                        startsWith: 'Starts with',
                        endsWith: 'Ends with',
                        exists: 'Is set',
                        notExists: 'Not set',
                        includes: 'Includes any',
                        includesAll: 'Includes all',
                        notIncludes: 'Not includes any',
                        notIncludesAll: 'Not includes all',
                        empty: 'Empty list',
                        notEmpty: 'Not empty list',
                    },
                    objectid: {
                        equals: 'Equals',
                        notEquals: 'Does not equal',
                        exists: 'Is set',
                        notExists: 'Not set',
                        includes: 'Includes any',
                        includesAll: 'Includes all',
                        notIncludes: 'Not includes any',
                        notIncludesAll: 'Not includes all',
                        empty: 'Empty list',
                        notEmpty: 'Not empty list',
                    },
                    number: {
                        equals: 'Equals',
                        notEquals: 'Does not equal',
                        greaterThan: 'Greater than',
                        greaterEquals: 'Greater than or equal',
                        lessThan: 'Less than',
                        lessEquals: 'Less than or equal',
                        between: 'Between',
                        notBetween: 'Not between',
                        exists: 'Is set',
                        notExists: 'Not set',
                    },
                    date: {
                        daysAgo: 'Days',
                        exactlyDaysAgo: 'Exactly ago',
                        moreThanDaysAgo: 'More than ago',
                        lessThanDaysAgo: 'Less than ago',
                        moment: 'Relative date',
                        momentValue: {
                            beforeToday: 'Before today',
                            beforeOrAtToday: 'Before or at today',
                            afterToday: 'After today',
                            afterOrAtToday: 'After or at today',
                        },
                        equals: 'At',
                        notEquals: 'Not at',
                        greaterThan: 'After',
                        greaterEquals: 'After or at',
                        lessThan: 'Before',
                        lessEquals: 'Before or at',
                        between: 'Between',
                        notBetween: 'Not between',
                        exists: 'Is set',
                        notExists: 'Not set',
                    },
                    bool: {
                        isTrue: 'Is true',
                        isFalse: 'Is false',
                        exists: 'Is set',
                        notExists: 'Not set',
                    },
                    any: {
                        exists: 'Is set',
                        notExists: 'Not set',
                    },
                },
            },
            user: {
                user: {
                    common: {
                        groupName: 'People',
                        userId: 'User id',
                        name: 'Name',
                        email: 'Email',
                        language: 'Language',
                        bio: 'Bio',
                        pushToken: 'Push token',
                        organizationsCount: 'Organization count',
                        globalPermissions: 'Global permissions',
                        createdAt: 'Created at',
                        lastSeen: 'Last seen',
                    },
                    organization: {
                        type: 'Type',
                        accessLevels: {
                            owner: 'Owner',
                            organization: 'Management',
                            contact: 'Community-App',
                            guest: 'Guest',
                        },
                        portal: 'Portal',
                        organizationPermissions: 'Management',
                        role: 'Role',
                        contacts: 'Contacts',
                        groups: {
                            user: 'User',
                            access: 'Access',
                            lastSeenAt: 'Last seen at',
                        },
                    },
                    globalPermissions: {
                        'organization.read': 'Organization read',
                        'organization.billing': 'Organization billing',
                        'organization.write': 'Organization write',
                        'user.read': 'User read',
                        'user.write': 'User write',
                        'auditLog.read': 'Audit log read',
                        'community.admin': 'Community admin',
                        'feedback.admin': 'Feedback admin',
                        'customer.reportType.admin': 'Customer report type admin',
                        'customer.report': 'Customer report',
                        'customer.report.admin': 'Customer report admin',
                        'customer.report.billing': 'Customer report billing',
                    },
                },
            },
            dispatchDocument: {
                createdAt: 'Date',
                sendStatus: 'Status',
                receipt: 'Receipt',
                recipient: {
                    name: 'Recipient',
                    email: 'Email',
                    address: 'Address',
                },
                sendMethod: 'Method',
                sentAt: 'Sent at',
                openedAt: 'Opened at',
                sendPrice: 'Cost',
                type: 'Type',
            },
            dispatchDocumentType: {
                receipt: 'Receipt',
                donation: 'Donation',
                proof: 'Required document',
                certificate: 'Certificate',
                pass: 'Pass',
                booking: 'Booking',
                message: 'Message',
                mailing: 'Mailing',
                applicationForm: 'Application form',
                contactRule: 'Automation',
                questionnaireForm: 'Questionnaire',
            },
            dispatchDocumentTypeViewName: {
                receipt: 'Outgoing receipts',
                donation: 'Donation confirmations',
                proof: 'Required document requests & confirmations',
                certificate: 'Certificate confirmations',
                pass: 'Issued digital passes',
                booking: 'Booking',
                mailing: 'Mailings',
                message: 'Messages',
                applicationForm: 'Application forms',
                contactRule: 'Automations',
                questionnaireForm: 'Questionnaires',
            },
            misc: {
                integration: {
                    businessCentral: {
                        customerNumberRequired: 'A customer number mapping is required',
                    },
                },
                questionnaireForm: {
                    statusValues: {
                        draft: 'Draft',
                        active: 'Active',
                        paused: 'Paused',
                        closed: 'Completed',
                    },
                },
                questionnaireSubmission: {
                    properties: {
                        submission: {
                            groupName: 'Submission',
                            createdAt: 'Submitted at',
                            questionnaireForm: 'Questionnaire',
                            status: 'Status',
                            emailConfirmed: 'Email confirmed',
                            dispatchStatus: 'Dispatch status',
                        },
                    },
                },
            },
            dispatchDocumentSendMethod: {
                none: 'No sending',
                auto: 'Auto-Send (Email -> Postal)',
                email: 'Send by Email',
                postal: 'Send by post',
            },
            dispatchDocumentSendStatus: {
                none: {
                    status: 'Will not be sent',
                    description: 'The document was manually marked to be not sent at all',
                },
                manual: {
                    status: 'Send by post',
                    description: 'The letter needs to be printed and send by post by you then you can mark it as sent.',
                },
                dispatching: {
                    status: 'Dispatching',
                    description: 'The document is waiting in the queue to be dispatched',
                },
                dispatched: {
                    status: 'Dispatched',
                    description: 'The document was dispatched so it was received by the actual processor',
                },
                sent: {
                    status: 'Was sent',
                    description: 'The document was sent to the recipient by the processor',
                },
                opened: {
                    status: 'Was opened',
                    description: 'The document was read / opened by the recipient',
                },
                failed: {
                    status: 'Has failed',
                    description: 'The document failed to be sent',
                },
            },
            dispatchDocumentSendError: {
                NO_CREDITS_ENOUGH: {
                    status: 'Insufficient credits',
                    description:
                        'You do not have enough credits to send the letter online. Recharge your credits and then you can resend the letter',
                },
            },
            donation: {
                donationType: {
                    cash: 'Cash',
                    membershipFee: 'Membership fee',
                    assetStock: 'Asset stock',
                    refund: 'Refund',
                    nonCash: 'Non-cash',
                },
                sampleTexts: {
                    donatorName: 'Jon Doe',
                    details1: 'c/o Jane Doe',
                    details2: 'Apartment 4',
                    addressLine: 'Sample Street 123',
                    city: 'Sample City',
                    zip: '12345',
                },
            },
            austrianDonationSubmission: {
                reportingYear: 'Reporting year',
                messageRefID: 'Reference',
                exportDate: 'Export date',
                exportFile: 'Exported file',
                returnFile: 'Returned file',
                submissionType: 'Submission type',
                ignoreDebtorsMissingBirthDate: 'Ignore debtors missing birth dates',
                ignoreSubmitedDebtors: 'Ignore submitted debtors',
                submissionTypeValues: {
                    E: 'Initial submission',
                    A: 'Amendment submission',
                    S: 'Cancellation submission',
                },
                status: 'Status',
                statusValues: {
                    error: 'Error',
                    donator_creating: 'Creating vbPK file',
                    donator_available: 'vbPK file ready',
                    donator_returnedProcessing: 'Processing returned vbPK file',
                    donator_returnedError: 'Returned vbPK error',
                    donator_returnedPartialSuccess: 'Returned vbPK partial success',
                    donator_returnedSuccess: 'Returned vbPK success',
                    donations_creating: 'Creating donations report',
                    donations_available: 'Donations report ready',
                    donations_returnedProcessing: 'Processing returned donations report',
                    donations_returnedError: 'Returned donations report error',
                    donations_returnedPartialSuccess: 'Returned donations partial success',
                    donations_returnedSuccess: 'Returned donations success',
                },
                errorCodeValues: {
                    'ERR-F001': 'The Fastnr_Fon_Tn must be valid, 9-digit and present.',
                    'ERR-F003':
                        'The tax number entered in the Fastnr_Fon_Tn field is not assigned to the FinanzOnline participant.',
                    'ERR-F008': 'The Fastnr_Org must be valid, 9-digit and present.',
                    'ERR-F009': 'There is no authorization for the transmission (Fastnr_Fon_Tn).',
                    'ERR-F010': 'There is no authorization for the transmitted year (Fastnr_Org).',
                    'ERR-F011':
                        'The donation abbreviation used in the transmission (in the transmission type field) does not match the type of donation organization registered with the tax office.',
                    'ERR-F012': 'The service provider is not authorized to make a transmission for the organization.',
                    'ERR-F013':
                        'The period must not be in the future and not in the current year. The first period is 2017.',
                },
            },
            finApiBankConnection: {
                defaultName: 'Bank connection #{{connectionId}}',
            },
            crm: {
                application: {
                    submission: {
                        status: {
                            unread: 'Unread',
                            pending: 'Pending',
                            approved: 'Approved',
                            rejected: 'Rejected',
                        },
                        reviewStatus: {
                            none: 'No review',
                            pending: 'In review',
                            accepted: 'Accepted',
                            rejected: 'Rejected',
                        },
                        emailConfirmed: {
                            pending: 'Not confirmed',
                            confirmed: 'Confirmed',
                        },
                        properties: {
                            submission: {
                                groupName: 'Submission',
                                createdAt: 'Submitted at',
                                applicationForm: 'Application form',
                                status: 'Status',
                                emailConfirmed: 'Email confirmed',
                                dispatchStatus: 'Dispatch status',
                                reviewStatus: 'Review status',
                                reviewer: 'Reviewer',
                            },
                        },
                    },
                },
                contact: {
                    contactType: {
                        member: {
                            single: 'Member',
                            multi: 'Members',
                            contactNumber: 'Membership number',
                        },
                        address: {
                            single: 'Address',
                            multi: 'Addresses',
                            contactNumber: 'Address number',
                        },
                        customer: {
                            single: 'Customer',
                            multi: 'Customers',
                            contactNumber: 'Customer number',
                        },
                        sponsor: {
                            single: 'Sponsor',
                            multi: 'Sponsors',
                            contactNumber: 'Sponsor number',
                        },
                        contributor: {
                            single: 'Contributor',
                            multi: 'Contributors',
                            contactNumber: 'Contributor number',
                        },
                        employee: {
                            single: 'Employee',
                            multi: 'Employees',
                            contactNumber: 'Employee number',
                        },
                        supplier: {
                            single: 'Supplier',
                            multi: 'Suppliers',
                            contactNumber: 'Supplier number',
                        },
                    },
                    contactSegment: {
                        defaultSegments: {
                            members: {
                                name: 'Current members',
                                hint: 'All members that have already entered and did not yet leave',
                            },
                            futureMembers: {
                                name: 'Future members',
                                hint: 'All members that will enter in the future',
                            },
                            resignedMembers: {
                                name: 'Resigned members',
                                hint: 'All members that already have left',
                            },
                        },
                    },
                    contactGender: {
                        female: 'Female',
                        male: 'Male',
                        diverse: 'Diverse',
                        none: 'None',
                    },
                    contactInstitutionType: {
                        business: 'Business',
                        club: 'Club',
                        association: 'Association',
                        city: 'City',
                        other: 'Other',
                    },
                    contact: {
                        common: {
                            groupName: 'Common',
                            type: 'Type',
                            name: 'Name',
                            createdAt: 'Created at',
                            updatedAt: 'Updated at',
                            language: 'Language',
                            tags: 'Tags',
                            activeDepartments: 'Active departments',
                            segments: 'Segments',
                        },
                        user: {
                            groupName: 'User',
                            actor: {
                                userId: 'Has user account',
                                name: 'Name',
                            },
                            claimedAt: 'Claimed at',
                        },
                        connect: {
                            groupName: 'Community-App',
                            joinedAt: 'Network: Joined at',
                            reviewedAt: 'Network: Reviewed at',
                            remindedAt: 'Network: Reminded at',
                        },
                        person: {
                            groupName: 'Person',
                            salutation: 'Salutation',
                            title: 'Title',
                            gender: 'Gender',
                            firstName: 'First name',
                            lastName: 'Last name',
                            birthdate: 'Birthdate',
                            yearOfBirth: 'Year of birth',
                            age: 'Age',
                            institutionName: 'Organization',
                        },
                        institution: {
                            groupName: 'Organization',
                            type: 'Type',
                            name: 'Name',
                        },
                        communication: {
                            groupName: 'Communication',
                            email: 'Email',
                            phone: 'Phone',
                            mobilePhone: 'Mobile phone',
                            mailing: 'Receives mailings',
                            mailingTags: 'Limit to mailing tags',
                        },
                        eventim: {
                            groupName: 'Eventim',
                            tixxId: 'Tixx ID',
                        },
                        alternativeCommunications: {
                            groupName: 'Alternative communication',
                            name: 'Name of contact',
                            description: 'Contact description',
                        },
                        departments: {
                            groupName: 'Departments',
                            department: 'Department',
                            name: 'Name',
                            entryAt: 'Entry',
                            exitAt: 'Exit',
                        },
                        groups: {
                            groupName: 'Groups',
                            group: 'Group',
                            type: 'Group type',
                            name: 'Name',
                            number: 'Number',
                            contacts: 'Contacts count',
                            contactSegmentCounters: 'Segment contact counter',
                        },
                        proofs: {
                            groupName: 'Required documents',
                            type: 'Required document type',
                            typeName: 'Type name',
                            status: 'Status',
                        },
                        certificates: {
                            groupName: 'Certificates',
                            typeLabel: 'Certificate type',
                            type: {
                                sampleTexts: {
                                    name: 'Training certificate',
                                },
                            },
                            typeName: 'Type name',
                            status: 'Status',
                        },
                        member: {
                            groupName: 'Membership',
                            entryAt: 'Entry',
                            exitAt: 'Exit',
                            terminatedAt: 'Terminated at',
                            terminationReason: 'Termination reason',
                            membershipDuration: 'Membership duration',
                        },
                        employee: {
                            groupName: 'Employee',
                            type: 'Type',
                            typeValue: {
                                internal: 'Internal',
                                external: 'External',
                            },
                            status: 'Status',
                            statusValue: {
                                active: 'Active',
                                inactive: 'Inactive',
                                onboarding: 'Onboarding',
                                leave: 'Leave',
                            },
                            position: 'Position',
                            probationStartAt: 'Probation start',
                            probationEndAt: 'Probation end',
                            employmentStartAt: 'Employment start',
                            employmentEndAt: 'Employment end',
                            contractEndAt: 'Contract end',
                            terminatedAt: 'Terminated at',
                            lastWorkingDayAt: 'Last working day',
                        },
                        contributor: {
                            groupName: 'Contributor',
                            status: 'Status',
                            statusValue: {
                                active: 'Active',
                                inactive: 'Inactive',
                                onboarding: 'Onboarding',
                                paused: 'Paused',
                            },
                            roleTitle: 'Role title',
                            contributionStartAt: 'Contribution start',
                            contributionEndAt: 'Contribution end',
                        },
                        debtor: {
                            groupName: 'Debtor',
                            account: 'Account',
                            name: 'Name',
                            receiptSendMethod: 'Receipt Delivery',
                            paymentMethodType: 'Payment Method',
                            sepaDirectDebitMandate: {
                                iban: 'IBAN',
                                ibanMasked: 'IBAN (masked)',
                                bic: 'BIC',
                                sepaMandateId: 'Mandate ID',
                                sepaMandateSignatureDate: 'Signature Date',
                                accountHolderName: 'Deviating Account holder name',
                            },
                        },
                        creditor: {
                            groupName: 'Creditor',
                            account: 'Account',
                            name: 'Name',
                        },
                        contracts: {
                            groupName: 'Contracts',
                            contract: 'Contract',
                            startAt: 'Start',
                            endAt: 'End',
                            pauseStartAt: 'Pause start',
                            pauseEndAt: 'Pause end',
                            lastPaidPeriodStartAt: 'Last paid period › Start at',
                            lastPaidPeriodEndAt: 'Last paid period › End at',
                            lastCollectionAdjustments: 'Last collection › Adjustments',
                            plan: 'Plan',
                            paymentInterval: 'Payment interval',
                        },
                        sampleTexts: {
                            organizationName: 'Campai',
                            firstName: 'Jon',
                            lastName: 'Doe',
                            institutionName: 'Campai',
                            salutation: 'Mr.',
                            title: 'Dr.',
                            email: 'jon.doe@example.com',
                            phone: '+49 123 456789',
                            mobilePhone: '+49 123 4567890',
                            details1: 'c/o Jane Doe',
                            details2: 'Apartment 4',
                            addressLine: 'Sample Street 123',
                            city: 'Sample City',
                            state: 'Sample State',
                            zip: '12345',
                            greetingsPerson: 'Dear Jon Doe',
                        },
                    },
                    customFields: 'Custom fields',
                    otherGroup: 'Other',
                },
                group: {
                    group: {
                        group: {
                            groupName: 'Group',
                            number: 'Number',
                            contacts: 'Contacts',
                            name: 'Name',
                            description: 'Description',
                            segments: 'Segments',
                            contactSegmentCounters: 'Segment contact counter',
                        },
                    },
                    membership: {
                        member: {
                            groupName: 'Member',
                            contactName: 'Contact',
                            createdAt: 'Added at',
                        },
                    },
                },
                contract: {
                    contract: {
                        contractStatus: {
                            collect: 'To collect',
                            active: 'Active',
                            upcoming: 'Upcoming',
                            paused: 'Paused',
                            termination: 'Terminating',
                            ended: 'Ended',
                            failed: 'Failed',
                            collecting: 'Collecting',
                        },
                        contactDebtor: {
                            groupName: 'Contact / Debtor',
                            contactName: 'Contact',
                            debtorName: 'Debtor',
                            debtorAccount: 'Account',
                            debtorPaymentMethodType: 'Payment method',
                        },
                        contract: {
                            groupName: 'Contract',
                            startAt: 'Start',
                            endAt: 'End',
                            terminatedAt: 'Termination',
                            pauseStartAt: 'Pause start',
                            pauseEndAt: 'Pause end',
                            createdAt: 'Created at',
                            fixedAmount: 'Fixed amount',
                            totalCollectedAmount: 'Total',
                        },
                        plan: {
                            groupName: 'Plan',
                            name: 'Name',
                            tags: 'Tags',
                            interval: 'Interval',
                        },
                        collection: {
                            collectAt: 'Collect at',
                            periodStart: 'From',
                            periodEnd: 'To',
                            amount: 'Amount',
                            description: 'Description',
                            voucherName: 'Voucher',
                            voucherAmount: 'Amount',
                            counter: 'Counter',
                        },
                        nextCollection: {
                            groupName: 'Next collection',
                            contractCollectionId: 'Contract collection',
                        },
                        lastCollection: {
                            groupName: 'Last collection',
                        },
                    },
                    collection: {
                        collection: {
                            groupName: 'Collection',
                            createdAt: 'Collected at',
                            status: 'Status',
                            nextAction: 'Next action',
                            contractCounter: 'Contracts',
                            statusValue: {
                                collecting: 'Collecting',
                                draft: 'Draft',
                                completed: 'Completed',
                                sent: 'Sent',
                                partialPaid: 'Partial paid',
                                paid: 'Paid',
                            },
                            statusInfo: {
                                collecting: 'The contracts are being collected',
                                draft: 'The receipts are being completed and there is at least one draft receipt',
                                completed: 'The receipts are completed',
                                sent: 'The receipts are sent',
                                partialPaid: 'The payments are being made',
                                paid: 'The payments are completed',
                            },
                            nextActionValue: {
                                complete: 'Complete receipts',
                                send: 'Send receipts',
                                collectDirectDebits: 'Collect direct debits',
                                collectOnlineDebits: 'Collect Online Debits',
                                charge: 'Charge open amount',
                            },
                        },
                        receiptCounters: {
                            groupName: 'Receipt',
                            total: 'Total',
                            draft: 'Draft',
                            completed: 'Completed',
                            sent: 'Sent',
                        },
                        amounts: {
                            groupName: 'Amounts',
                            totalAmount: 'Total',
                            openAmount: 'Open',
                            paidAmount: 'Paid',
                        },
                    },
                },
                plan: {
                    period: {
                        interval: {
                            month: 'Monthly',
                            quarter: 'Quarterly',
                            halfYear: 'Half yearly',
                            year: 'Yearly',
                            custom: 'Custom',
                            flexible: 'Flexible',
                        },
                        mode: {
                            interval: 'By interval',
                            range: 'By range',
                        },
                    },
                    collection: {
                        period: {
                            start: 'Start',
                            end: 'End',
                        },
                        relative: {
                            at: 'None',
                            weekStart: 'Week start',
                            weekEnd: 'Week end',
                            monthStart: 'Month start',
                            monthEnd: 'Month end',
                            quarterStart: 'Quarter start',
                            quarterEnd: 'Quarter end',
                            halfYearStart: 'Half year start',
                            halfYearEnd: 'Half year end',
                            yearStart: 'Year start',
                            yearEnd: 'Year end',
                        },
                        shiftMode: {
                            before: 'Before',
                            after: 'After',
                        },
                        absoluteAnchor: {
                            interval: 'Interval',
                            period: 'Period',
                        },
                        deviantMode: {
                            before: 'Before contract',
                            after: 'After contract',
                            beforeIntervalStart: 'Before interval start',
                            afterIntervalStart: 'After interval start',
                            beforeIntervalEnd: 'Before interval end',
                            afterIntervalEnd: 'After interval end',
                            immediate: 'Immediately',
                            next: 'Only next period',
                        },
                    },
                    planType: {
                        membershipFee: 'Membership fee',
                        donation: 'Donation',
                        contract: 'Contract',
                    },
                    planChargeValueOperator: {
                        set: 'Set amount',
                        add: 'Add amount',
                        subtract: 'Subtract amount',
                        addPercentage: 'Add percentage',
                        subtractPercentage: 'Subtract percentage',
                        multiplyBy: 'Multiply by amount',
                        divideBy: 'Divide by amount',
                    },
                    planChargeRelativeDate: {
                        periodStart: 'Relative to period start',
                        periodEnd: 'Relative to period end',
                        periodCenter: 'Relative to period center',
                        collectAt: 'Relative to collection date',
                    },
                    planChargeAmountProRataMode: {
                        start: 'At the start',
                        end: 'At the end',
                        startEnd: 'At the start and end',
                    },
                    plan: {
                        plan: {
                            groupName: 'Plan',
                            name: 'Name',
                            tags: 'Tags',
                        },
                        period: {
                            groupName: 'Period',
                            interval: 'Interval',
                            mode: 'Mode',
                            count: 'Stop after',
                        },
                        collection: {
                            groupName: 'Collection',
                            period: 'Period',
                            relative: 'Relative',
                            shiftMode: 'Shift mode',
                            shiftTimeValue: 'Shift value',
                            shiftTimeUnit: 'Shift unit',
                            deviantMode: 'Deviant mode',
                            deviantTimeValue: 'Deviant value',
                            deviantTimeUnit: 'Deviant unit',
                            absoluteDate: 'Fixed day',
                            absoluteMonth: 'Fixed month',
                            absoluteAnchor: 'Fixed within',
                            daysToDue: 'Days to due',
                        },
                        charge: {
                            groupName: 'Charge',
                            amount: {
                                price: 'Price',
                                customField: 'Custom field',
                                operator: 'Operator',
                                description: 'Description',
                                proRataDivisor: 'Pro-rata',
                            },
                            options: {
                                propertyName: 'Options',
                                price: 'Price',
                                customField: 'Custom field',
                                operator: 'Operator',
                                description: 'Description',
                            },
                            adjustments: {
                                propertyName: 'Adjustments',
                                operator: 'Operator',
                                amount: 'Amount',
                            },
                        },
                        billing: {
                            groupName: 'Billing',
                            sepaDirectDebitPurpose: 'Direct debit purpose',
                            deleteContractAfterEnd: 'Delete contract after end',
                        },
                    },
                },
                proofType: {
                    validityModes: {
                        oneTime: 'Forever valid',
                        byDate: 'To a given date',
                        byInterval: 'For a certain interval',
                    },
                    validity: {
                        dueBeforeDaysMustBeGreaterThanOverdueBeforeDays:
                            'Due before days must be greater than overdue before days',
                    },
                },
                proof: {
                    proofStatus: {
                        required: 'Required',
                        requested: 'Requested',
                        confirmed: 'Confirmed',
                        due: 'Due',
                        overdue: 'Overdue',
                        expired: 'Expired',
                    },
                    proof: {
                        proof: {
                            groupName: 'Required document',
                            groupPath: 'Required document',
                            status: 'Status',
                            typeName: 'Type',
                            contactName: 'Contact',
                            validTo: 'Valid to',
                            requestDispatchStatusSendStatus: 'Request status',
                            confirmationDispatchStatusSendStatus: 'Confirmation status',
                        },
                        confirmation: {
                            groupName: 'Confirmation',
                            groupPath: 'Confirmation',
                            issuedAt: 'Issued at',
                            issuingAuthority: 'Issuing authority',
                            reviewedAt: 'Reviewed at',
                            reviewedBy: 'Reviewed by',
                            notes: 'Notes',
                            documents: 'Documents',
                        },
                    },
                },
                certificate: {
                    certificateStatus: {
                        valid: 'Valid',
                        willExpire: 'Will expire',
                        expired: 'Expired',
                    },
                    certificate: {
                        certificate: {
                            groupName: 'Certificate',
                            groupPath: 'Certificate',
                            status: 'Status',
                            typeName: 'Type',
                            contactName: 'Contact',
                            validTo: 'Valid to',
                            issuedAt: 'Issued at',
                            issuedBy: 'Issued by',
                            documents: 'Documents',
                            confirmationDispatchStatusSendStatus: 'Confirmation status',
                            notes: 'Notes',
                        },
                    },
                },
                crmSettings: {
                    stockReport: {
                        typeValues: {
                            singleSport: 'Single sport club',
                            multipleSports: 'Multiple sports club',
                        },
                        clubNumber: 'Club number',
                        contactPerson: 'Contact person',
                        associationNumber: 'Association number',
                        sportNumber: 'Sport number',
                    },
                    contactNumberFormats: {
                        typePrefix: {
                            member: 'MB',
                            address: 'AD',
                            employee: 'EM',
                            contributor: 'CT',
                            customer: 'CU',
                            sponsor: 'SP',
                            supplier: 'SU',
                        },
                    },
                },
                passes: {
                    passTemplate: {
                        statistics: {
                            pending: 'Pending',
                            installed: 'Installed',
                            uninstalled: 'Uninstalled',
                            printed: 'Printed',
                        },
                    },
                    issuedPass: {
                        common: {
                            groupName: 'Common',
                            passTemplateName: 'Pass',
                            passTemplatePrintable: 'Printable',
                        },
                        holder: {
                            groupName: 'Holder',
                            name: 'Name',
                            email: 'Email',
                        },
                        status: {
                            groupName: 'Status',
                            dispatch: 'Dispatch',
                            installation: 'Installation',
                            printing: 'Printing',
                        },
                        digitalCard: {
                            groupName: 'Digital card',
                        },
                        printedCard: {
                            groupName: 'Printed card',
                        },
                        printExport: {
                            issuedPass: 'Issued pass',
                            internalBarcode: 'Internal barcode',
                            contact: 'Contact',
                        },
                        dispatchStatus: 'Dispatch status',
                        dispatchStatusValue: {
                            scheduled: 'Scheduled',
                            scheduledInfo: 'The email will be dispatched to the holder at the scheduled time',
                            none: 'Will not be sent',
                            noneInfo: 'The email will not be dispatched because the holder has no email address',
                            scheduledTo: 'Scheduled to',
                        },
                        installationStatus: 'Installation status',
                        installationStatusValue: {
                            pending: 'Pending',
                            installed: 'Installed',
                            uninstalled: 'Uninstalled',
                        },
                        printStatus: 'Print status',
                        printStatusValue: {
                            pending: 'Pending',
                            printed: 'Printed',
                        },
                        revokedAt: 'Revoked at',
                        revokedBy: 'Revoked by',
                        createdAt: 'Created at',
                        createdBy: 'Created by',
                        printedAt: 'Printed at',
                        printedBy: 'Printed by',
                        receiptNumber: 'Receipt number',
                        validFrom: 'Valid from',
                        validTo: 'Valid to',
                        validityStatus: 'Validity',
                        validityStatusValue: {
                            valid: 'Valid',
                            validInfo: 'The pass is within its validity period',
                            invalid: 'Invalid',
                            invalidInfo: 'The pass is out of its validity period',
                            upcoming: 'Upcoming',
                            upcomingInfo: 'The pass is not yet within its validity period',
                            revoked: 'Revoked',
                            revokedInfo: 'The pass was revoked manually or automatically due to contact sections',
                        },
                    },
                },
                conversation: {
                    mailboxValues: {
                        inbox: 'Inbox',
                        spam: 'Spam',
                        archived: 'Archived',
                        deleted: 'Deleted',
                    },
                },
                conversationMessage: {
                    mailStatusValues: {
                        pending: 'Pending',
                        delivered: 'Delivered',
                        error: 'Failed',
                    },
                },
                mailing: {
                    messageTypeOptions: {
                        email: 'Email',
                        letter: 'Letter',
                    },
                    groupName: 'Message template',
                    name: 'Name',
                    description: 'Description',
                    status: 'Status',
                    type: 'Type',
                    email: {
                        subject: 'Email subject',
                        previewText: 'Inbox text preview',
                        tracking: 'Track email opens',
                        unsubscribe: 'Allow unsubscriptions',
                        replyTo: 'Reply-to address',
                    },
                    letter: {
                        sendColorPostal: 'Send color postal',
                        sendWithCheaperMethod: 'Send with cheaper method',
                    },
                    onlySendToMissingEmailAddresses: 'Only for recipients missing email addresses',
                    scope: 'Scope',
                    draft: 'Draft',
                    contactType: 'Contact type',
                    tags: 'Mailing tags',
                    scheduledAt: 'Scheduled at',
                    mailingStatusValues: {
                        draft: 'Draft',
                        send: 'Send',
                        sending: 'Sending',
                        delivered: 'Delivered',
                    },
                    total: 'Total recipients',
                    costs: 'Costs',
                    none: 'None',
                    manual: 'Manual',
                    dispatching: 'Dispatching',
                    dispatched: 'Dispatched',
                    sent: 'Sent',
                    opened: 'Opens',
                    failed: 'Failed',
                    clicks: 'Clicks',
                    unsubscribes: 'Unsubscribes',
                },
                messageTemplate: {
                    groupName: 'Message template',
                    name: 'Name',
                    description: 'Description',
                    type: 'Type',
                    email: {
                        subject: 'Email subject',
                        previewText: 'Inbox text preview',
                    },
                    letter: {
                        sendColorPostal: 'Send color postal',
                        sendWithCheaperMethod: 'Send with cheaper method',
                    },
                    scope: 'Scope',
                    draft: 'Draft',
                    contactType: 'Contact type',
                    allowedPlaces: 'Allowed at',
                    allowedPlacesOptions: {
                        mailings: 'Mailings',
                        messages: 'Messages',
                        automations: 'Automations',
                    },
                    scopeOptions: {
                        local: 'Local',
                        global: 'Global',
                    },
                },
                rule: {
                    groupName: 'Automation',
                    contactType: 'Contact type',
                    contactSegments: 'Contact segments',
                    trigger: 'Trigger',
                    actions: 'Actions',
                    action: 'Action',
                    executionsCount: 'Executions',
                    lastExecutedAt: 'Last executed at',
                    triggerType: {
                        created: 'Created',
                        updated: 'Updated',
                        birthday: 'Birthday',
                        time: 'Recurring time',
                        'member:entryAt': 'Member entry',
                        'member:exitAt': 'Member exit',
                        'member:terminatedAt': 'Member terminated at',
                        'member:membershipDuration': 'Membership duration',
                        'departments:entryAt': 'Department entry',
                        'departments:exitAt': 'Department exit',
                    },
                    actionType: {
                        sendContactEmail: 'Send contact email',
                        sendEmail: 'Send email to addresses',
                    },
                    scheduleLabel: 'Schedule',
                    schedule: {
                        none: 'No schedule',
                        shiftMode: {
                            before: 'before',
                            after: 'after',
                        },
                        shiftTime: 'Shift',
                        time: 'Time',
                        timeValue: 'At {{time}}',
                    },
                    createRuleProperties: {
                        affectedProperties: {
                            affectedDepartment: {
                                groupName: 'Affected department',
                                name: 'Affected department name',
                                entryAt: 'Affected department entry at',
                                exitAt: 'Affected department exit at',
                            },
                        },
                    },
                },
                contactRuleExecution: {
                    statusValues: {
                        pending: 'Pending',
                        executing: 'Executing',
                        executed: 'Executed',
                    },
                    actionsResult: {
                        statusValues: {
                            success: 'Success',
                            failed: 'Failed',
                            unapplicable: 'Unapplicable',
                        },
                    },
                },
                voucher: {
                    common: {
                        groupName: 'Common',
                        name: 'Name',
                        code: 'Code',
                        amount: 'Amount',
                        interval: 'Interval',
                        startAt: 'Start',
                        endAt: 'End',
                    },
                    contact: {
                        groupName: 'Contact',
                        name: 'Name',
                    },
                    contract: {
                        groupName: 'Contract',
                        name: 'Name',
                    },
                    interval: {
                        month: 'Monthly',
                        quarter: 'Quarterly',
                        halfYear: 'Half-year',
                        year: 'Yearly',
                        oneTime: 'One-time',
                    },
                },
            },
            sales: {
                opportunity: {
                    groupName: 'Opportunity',
                    name: 'Name',
                    stage: 'Stage',
                    contact: 'Contact',
                    expectedValue: 'Expected revenue',
                    salesChance: 'Sales probability (%)',
                    expectedCloseAt: 'Expected close',
                    responsibleUser: 'Responsible user',
                    products: 'Products',
                    product: 'Product',
                    quantity: 'Quantity',
                    offerReceipt: 'Offer',
                    renewalOf: 'Renewal of',
                    notes: 'Notes',
                    lostReason: 'Lost reason',
                    createdAt: 'Created at',
                    updatedAt: 'Updated at',
                    stages: {
                        new: 'New',
                        qualified: 'Qualified',
                        offer: 'Offer',
                        negotiation: 'Negotiation',
                        won: 'Won',
                        lost: 'Lost',
                    },
                },
            },
            booking: {
                bookingType: {
                    event: 'Event',
                    resource: 'Resource',
                },
                bookingViewType: {
                    event: 'Events',
                    resource: 'Resources',
                },
                bookingViewStyle: {
                    group: 'Group',
                    list: 'List',
                    schedule: 'Schedule',
                    plan: 'Plan',
                    sites: 'Sites',
                },
                bookingCategory: {
                    name: 'Name',
                    description: 'Description',
                    availability: 'Available for',
                    availabilityValues: {
                        all: 'All',
                        events: 'Events',
                        resources: 'Resources',
                    },
                },
                bookingStatusValue: {
                    confirmed: 'Confirmed',
                    partialCancelation: 'Cancelation(s)',
                    canceled: 'Canceled',
                },
                bookingFormSettingsAddressMode: {
                    none: 'No address',
                    optional: 'Optional address',
                    required: 'Address is required',
                },
                bookingFormSettingsApplicantType: {
                    person: 'Persons only',
                    organization: 'Organizations only',
                    both: 'Persons and organizations',
                },
                booking: {
                    booking: {
                        groupName: 'Booking',
                        createdAt: 'Booked at',
                        bookingNumber: 'Number',
                        type: 'Type',
                        status: 'Status',
                        records: {
                            event: 'Attendees',
                            resource: 'Resources',
                            any: 'Amount',
                        },
                    },
                    payment: {
                        groupName: 'Payment',
                        status: 'Status',
                        paymentMethod: 'Method',
                        price: 'Price',
                        points: 'Points',
                        usedPunchCardNumber: 'Punch card no.',
                        discountCode: 'Discount code',
                    },
                    buyer: {
                        groupName: 'Buyer / Applicant',
                        name: 'Name',
                        personName: 'Person name',
                        email: 'Email',
                        phone: 'Phone',
                        address: 'Address',
                        user: 'User',
                    },
                },
                offerMode: {
                    disabled: 'Disabled',
                    private: 'Private',
                    protected: 'Protected',
                    public: 'Public',
                },
                event: {
                    event: {
                        event: {
                            groupName: 'Event',
                            title: 'Title',
                            description: 'Description',
                            tags: 'Tags',
                            categories: 'Categories',
                        },
                        schedule: {
                            groupName: 'Schedule',
                            from: 'Starts at',
                            to: 'Ends at',
                            timezone: 'Timezone',
                            recurring: 'Recurring',
                            status: 'Availability status',
                        },
                        place: {
                            groupName: 'Place',
                            type: 'Type',
                            site: 'Site',
                            resource: 'Resource',
                            address: 'Address',
                            url: 'Online URL',
                        },
                        booking: {
                            groupName: 'Booking',
                            mode: 'Offer mode',
                            offer: 'Offer',
                            attendeeMode: 'Attendee mode',
                            bookingRestriction: 'Booking restriction',
                            availableFrom: 'Bookable from',
                            availableTo: 'Bookable until',
                            maxAttendees: 'Maximum attendees',
                            permanent: 'Permanent booking',
                            waitlist: 'Waitlist',
                        },
                        instructors: {
                            groupName: 'Instructors',
                            name: 'Instructors',
                            instructor: 'Instructor',
                        },
                        statistics: {
                            groupName: 'Statistics',
                            attendees: 'Attendees',
                            teamMembers: 'Team members',
                            waitlisted: 'On waitlist',
                            offered: 'Offered',
                            pending: 'Pending',
                            confirmed: 'Confirmed',
                            canceled: 'Canceled',
                            checkedIn: 'Checked in',
                        },
                        status: {
                            groupName: 'Status',
                            canceledAt: 'Canceled at',
                        },
                        placeType: {
                            resource: 'Resource',
                            address: 'Address',
                            online: 'Online',
                        },
                        periodStatus: {
                            available: 'Available',
                            blockedResource: 'Blocked by resource',
                            blockedCalendar: 'Blocked by calendar',
                        },
                        attendeeMode: {
                            multiple: 'Multiple persons',
                            single: 'Single person',
                            team: 'Team',
                            rate: 'Rate',
                        },
                        bookingRestriction: {
                            soldOut: 'Sold out',
                            bookIntoWaitlist: 'Waitlist booking',
                            requireConfirmation: 'Confirmation required',
                        },
                    },
                    attendeeParticipantType: {
                        person: 'Person',
                        team: 'Team',
                    },
                    attendee: {
                        common: {
                            name: 'Name',
                            personName: 'Person name',
                            membershipNumber: 'Member',
                            user: 'User',
                            address: 'Address',
                            email: 'Email',
                            phone: 'Phone',
                        },
                        booking: {
                            groupName: 'Booking',
                            recordNumber: 'Number',
                            rate: 'Rate',
                            rateVariant: 'Variant',
                            serviceName: 'Services',
                        },
                        attendee: {
                            groupName: 'Attendee',
                        },
                        team: {
                            groupName: 'Team',
                        },
                        applicant: {
                            groupName: 'Applicant',
                        },
                        waitlist: {
                            groupName: 'Waitlist',
                            listNumber: 'Waiting-list number',
                            offerStatus: 'Offer status',
                        },
                        status: {
                            groupName: 'Status',
                            status: 'Status',
                            createdAt: 'Created at',
                            checkedInAt: 'Checked in at',
                            canceledAt: 'Canceled at',
                        },
                        statusValue: {
                            attendees: 'Attendees',
                            teams: 'Teams',
                            teamMembers: 'Team members',
                            waitlisted: 'On waitlist',
                            offered: 'Offered',
                            pending: 'Pending',
                            confirmed: 'Confirmed',
                            canceled: 'Canceled',
                            checkedIn: 'Checked in',
                        },
                        waitlistOfferStatusValue: {
                            manual: 'Manual',
                            pending: 'Pending',
                            accepted: 'Accepted',
                            rejected: 'Rejected',
                        },
                        tickets: {
                            groupName: 'Data of the ticket',
                        },
                        form: {
                            groups: {
                                tickets: 'Tickets',
                            },
                            fields: {
                                attendeeName: 'Attendee name',
                            },
                        },
                    },
                    eventOffer: {
                        team: {
                            labelDefault: 'Person(s)',
                        },
                    },
                },
                resource: {
                    resource: {
                        resource: {
                            groupName: 'Resource',
                            name: 'Name',
                            type: 'Type',
                            description: 'Description',
                            tags: 'Tags',
                            categories: 'Categories',
                            attachable: 'Attachable',
                            shared: 'Shared',
                        },
                        sites: {
                            groupName: 'Sites',
                            name: 'Sites',
                            site: 'Site',
                            address: 'Address',
                            timezone: 'Timezone',
                            stock: 'Stock',
                        },
                        booking: {
                            groupName: 'Booking',
                            mode: 'Offer mode',
                            offer: 'Offer',
                            slotSize: 'Booking slot size',
                            slotUnit: 'Booking slot unit',
                            minimumSlots: 'Minimum booking slots',
                            maximumSlots: 'Maximum booking slots',
                            availableOnWatchlist: 'Available on watchlist',
                            checkIn: 'Check-in available',
                        },
                        slotUnit: {
                            minutes: 'Minutes',
                            hours: 'Hours',
                            days: 'Days',
                        },
                    },
                    resourceType: {
                        place: {
                            single: 'Place',
                            multi: 'Places',
                        },
                        person: {
                            single: 'Person',
                            multi: 'Persons',
                        },
                        vehicle: {
                            single: 'Vehicle',
                            multi: 'Vehicles',
                        },
                        tool: {
                            single: 'Tool',
                            multi: 'Tools',
                        },
                        clothing: {
                            single: 'Clothing',
                            multi: 'Clothings',
                        },
                        object: {
                            single: 'Object',
                            multi: 'Objects',
                        },
                        other: {
                            single: 'Other',
                            multi: 'Others',
                        },
                    },
                    reservation: {
                        reservationType: {
                            booking: 'Booking',
                            event: 'Event',
                            custom: 'Custom',
                        },
                        statusValue: {
                            pending: 'Pending',
                            confirmed: 'Confirmed',
                            canceled: 'Canceled',
                            checkedIn: 'Checked in',
                            // --
                            reserved: 'Reserved',
                            maintenance: 'Maintenance',
                            blocked: 'Blocked',
                            outOfOrder: 'Out of order',
                            closed: 'Closed',
                            other: 'Other',
                        },
                        reservation: {
                            groupName: 'Reservation',
                            type: 'Type',
                            periodStart: 'From',
                            periodEnd: 'To',
                            site: 'Site',
                            quantity: 'Quantity',
                            resource: 'Resource',
                            description: 'Description',
                        },
                        booking: {
                            groupName: 'Booking',
                            applicant: {
                                groupName: 'Applicant',
                                name: 'Name',
                                membershipNumber: 'Member',
                                user: 'User',
                                email: 'Email',
                                phone: 'Phone',
                                address: 'Address',
                            },
                            recordNumber: 'Number',
                            rate: 'Rate',
                            rateVariant: 'Variant',
                            serviceName: 'Services',
                        },
                        status: {
                            groupName: 'Status',
                            status: 'Status',
                            createdAt: 'Created at',
                            checkedInAt: 'Checked in at',
                            canceledAt: 'Canceled at',
                        },
                        event: {
                            groupName: 'Event',
                            event: 'Event',
                        },
                    },
                    checkIn: {
                        checkIn: {
                            groupName: 'Check-In',
                            contact: 'Contact',
                            checkInAt: 'Checked in at',
                            checkOutAt: 'Checked out at',
                            key: 'Key',
                            options: 'Options',
                            site: 'Site',
                            resource: 'Resource',
                        },
                    },
                },
                issuedPunchCard: {
                    status: {
                        active: 'Active',
                        expired: 'Expired',
                        locked: 'Locked',
                        canceled: 'Canceled',
                    },
                },
            },
            finance: {
                account: {
                    debtor: {
                        blocked: 'Debtor was blocked on {{blockedAt}} by',
                        debtor: {
                            debtor: {
                                groupName: 'Debtor',
                                account: 'Account',
                                name: 'Name',
                                details: 'Details',
                                contact: 'Contact',
                            },
                            status: {
                                groupName: 'Status',
                                blockedAt: 'Blocked at',
                                blockMode: 'Block mode',
                                blockModes: {
                                    all: 'Full block',
                                    directDebit: 'Restrict payments',
                                },
                                blockedNote: 'Block note',
                                disableAutomaticReminders: 'Disable automatic reminders',
                                totalOwedFromDebtor: 'Owed from',
                                totalOwedFromDebtorInfo: 'Outstanding money the debtor owes you',
                                totalOwedToDebtor: 'Owed to',
                                totalOwedToDebtorInfo: 'Outstanding money you owe the debtor',
                                totalDeposit: 'Deposit',
                                totalDepositInfo:
                                    'The deposit money the debtor has left to be used as funds for invoices',
                            },
                            payment: {
                                groupName: 'Payment',
                                paymentMethodType: 'Payment method',
                                sepaDirectDebitMandate: {
                                    iban: 'IBAN',
                                    bic: 'BIC',
                                    sepaMandateId: 'Mandate ID',
                                    sepaMandateSignatureDate: 'Signature Date',
                                },
                            },
                            other: {
                                groupName: 'Other',
                                receiptSendMethod: 'Send method',
                                supplierNumber: 'Supplier number',
                                email: 'Email',
                                address: 'Address',
                            },
                        },
                    },
                    creditor: {
                        creditor: {
                            creditor: {
                                groupName: 'Creditor',
                                account: 'Account',
                                name: 'Name',
                                details: 'Details',
                            },
                            status: {
                                groupName: 'Status',
                                totalOwedToCreditor: 'Owed to',
                                totalOwedToCreditorInfo: 'Outstanding money you owe the creditor',
                                totalOwedFromCreditor: 'Owed from',
                                totalOwedFromCreditorInfo: 'Outstanding money the creditor owes you',
                            },
                            payment: {
                                groupName: 'Payment',
                                paymentMethodType: 'Payment method',
                                paymentMethodTypeValue: {
                                    cash: 'Cash',
                                    creditTransfer: 'Credit transfer',
                                },
                                creditTransfer: {
                                    iban: 'IBAN',
                                    bic: 'BIC',
                                    accountHolderName: 'Account holder',
                                },
                            },
                            other: {
                                groupName: 'Other',
                                supplierNumber: 'Supplier number',
                            },
                        },
                    },
                },
                accounting: {
                    account: {
                        number: 'Account number',
                        label: 'Description',
                        taxCode: 'Tax code',
                        bookable: 'Bookable',
                        datevAutomatic: 'Datev automatic',
                        group: {
                            capitalAsset: 'Capital assets',
                            liquidAsset: 'Liquid assets',
                            cash: 'Bank and cash registers',
                            equityCapital: 'Equity capital',
                            debtCapital: 'Debt capital',
                            revenue: 'Revenue',
                            expense: 'Expense',
                            static: 'Static neutral',
                            staticDebit: 'Static debit',
                            staticCredit: 'Static credit',
                            debtor: 'Debtor',
                            creditor: 'Creditor',
                        },
                    },
                    accountingPlanType: {
                        SKR42EUR: 'SKR 42 for Einnahmen-Überschuss-Rechnung',
                        SKR42GUV: 'SKR 42 for GuV/Bilanzierer',
                        SKR03GUV: 'SKR 03 for GuV/Bilanzierer',
                        SKR04GUV: 'SKR 04 for GuV/Bilanzierer',
                        SKR07EAR: 'SKR 07 for Einnahmen-Ausgaben-Rechnung (Österreich)',
                        SKR07GUV: 'SKR 07 for GuV/Bilanzierer (Österreich)',
                        USGUV: 'US chart of accounts',
                    },
                    posting: {
                        postingSource: {
                            manual: 'Single posting',
                            expense: 'Expense',
                            revenue: 'Revenue',
                            invoice: 'Invoice',
                            deposit: 'Deposit',
                            donation: 'Donation',
                            offer: 'Offer',
                            confirmation: 'Order confirmation',
                            cashRegister: 'Cash register',
                            cashTransaction: 'Cash transaction',
                            datevImport: 'Datev import',
                            forward: 'Forward',
                            postingReceipt: 'Posting receipt',
                            fixedAsset: 'Fixed asset',
                        },
                        posting: {
                            groupName: 'Single posting',
                            number: 'Number',
                            receiptDate: 'Date',
                            receiptNumber: 'Receipt-Number',
                            text: 'Description',
                            amount: 'Amount',
                            taxCode: 'Tax',
                            taxAmount: 'Tax amount',
                            debitAccount: 'Debit',
                            creditAccount: 'Credit',
                            costCenter1: 'Cost-Ct. 1',
                            costCenter2: 'Cost-Ct. 2',
                        },
                        dimensions: { groupName: 'Dimensions' },
                        other: {
                            groupName: 'Other',
                            reverse: 'Reverse',
                            committedAt: 'Committed at',
                            source: 'Source',
                            deliveryDate: 'Delivery date',
                            receiptFile: 'Receipt file',
                            taxDebitAccount: 'Tax debit',
                            taxCreditAccount: 'Tax credit',
                            isGross: 'Gross booking',
                        },
                    },
                },
                cash: {
                    onlineDebits: {
                        dispatchDue: {
                            developmentOnly: 'Online Debit collection processing can only be triggered in development',
                            notFound: 'Online Debit collection not found',
                        },
                        reasons: {
                            default: {
                                customerActionRequired:
                                    'The payment provider requires the customer to update or confirm the payment method.',
                                authenticationRequired:
                                    'The bank requires the customer to confirm this payment method.',
                                paymentMethodUnavailable: 'The payment method is no longer available.',
                                paymentAuthorizationInactive: 'The payment authorization is no longer active.',
                                paymentMethodExpired: 'The saved payment method has expired.',
                            },
                            paypal: {
                                payerActionRequired:
                                    'PayPal requires the customer to approve or update the payment method.',
                                payerCannotPay: 'The PayPal account cannot currently be charged.',
                                vaultTokenDeleted: 'The Online Debit is no longer active.',
                            },
                            stripe: {
                                cardDeclined: 'The card was declined.',
                                setupFailed:
                                    'The payment provider requires the customer to update or confirm the payment method.',
                            },
                        },
                    },
                    cashAccount: {
                        source: {
                            bank: 'Bank account',
                            paypal: 'Paypal account',
                            stripe: 'Stripe account',
                        },
                    },
                    cashTransaction: {
                        date: 'Date',
                        amount: 'Amount',
                        purpose: 'Purpose',
                        counterpartName: 'Counterpart name',
                        counterpartAccount: 'Counterpart account',
                    },
                    cashTransactionRule: {
                        matcher: {
                            type: 'Type',
                            amount: 'Amount',
                            purpose: 'Purpose',
                            counterpartName: 'Counterpart name',
                            counterpartAccount: 'Counterpart account',
                        },
                        outcome: 'Outcome',
                    },
                },
                receipts: {
                    receipt: {
                        receipt: {
                            receipt: {
                                groupName: 'Receipt',
                                type: 'Type',
                                invoiceType: 'Invoice type',
                                receiptNumber: 'Number',
                                receiptDate: 'Receipt date',
                                dueDate: 'Due date',
                                draft: 'Draft',
                                refund: 'Refund',
                                canceledBy: 'Canceled',
                            },
                            recipient: {
                                groupName: 'Recipient',
                                accountName: 'Name',
                                account: 'Account',
                                customerType: 'Type',
                                customerNumber: 'Number',
                                sendMethod: 'Send method',
                                receiptDispatchStatusSendStatus: 'Send status',
                            },
                            positions: {
                                groupName: 'Positions',
                                description: 'Description',
                                quantity: 'Quantity',
                                unit: 'Unit',
                                unitAmount: 'Amount',
                                taxCode: 'Tax',
                                discount: 'Discount',
                                amount: 'Total',
                                account: 'Account',
                                costCenter1: 'Cost-Ct 1',
                                costCenter2: 'Cost-Ct 2',
                            },
                            reminderStatus: {
                                groupName: 'Reminder status',
                                level: 'Level',
                                fees: 'Fees',
                                reminders: 'Reminders',
                            },
                            reminders: {
                                groupName: 'Reminders',
                                level: 'Level',
                                date: 'Date',
                                fee: 'Fee',
                                unpaidAmount: 'Unpaid',
                            },
                            payment: {
                                groupName: 'Payment',
                                isNet: 'Net',
                                totalGrossAmount: 'Total Gross',
                                totalAmountLeftToPay: 'To pay',
                                totalFeesAmount: 'Fees',
                                paidAt: 'Paid at',
                                paymentStatus: 'Payment status',
                                paymentDifference: 'Difference',
                                paymentMethod: 'Payment method',
                            },
                            payments: {
                                groupName: 'Payments',
                                date: 'Date',
                                amount: 'Amount',
                                account: 'Account',
                                cashTransaction: 'Transaction',
                            },
                            returnedPayments: {
                                groupName: 'Returned payments',
                                date: 'Date',
                                returnFee: 'Banking fee',
                                processingFee: 'Processing fee',
                                action: 'Action',
                                unpaidAmount: 'Unpaid amount',
                            },
                            cancelation: {
                                groupName: 'Cancelation',
                                canceledAt: 'Date',
                                canceledReason: 'Reason',
                                cancelReceiptNumber: 'Receipt number',
                            },
                            other: {
                                groupName: 'Other',
                                refundReceiptNumber: 'Refund-Number',
                                tags: 'Tags',
                                archived: 'Archived',
                                contractCollection: 'Contract collection',
                            },
                        },
                        returnedPaymentAction: {
                            retry: 'Retried charging',
                            manual: 'Manual transfer',
                            cancel: 'Canceled receipt',
                        },
                        type: {
                            expense: 'Expense',
                            expenses: 'Expenses',
                            expenseInfo: 'Receipt for some expense you made',
                            expenseRefund: 'Expense refund',
                            expensesRefund: 'Expense refunds',
                            expenseRefundInfo: 'Receipt if you got a refund for some expense',
                            revenue: 'Revenue',
                            revenues: 'Revenues',
                            revenueInfo: 'Simple receipt for a revenue you already made',
                            revenueRefund: 'Revenue refund',
                            revenuesRefund: 'Revenue refunds',
                            revenueRefundInfo: 'Receipt if you refunded a simple revenue you made',
                            offer: 'Offering',
                            offers: 'Offerings',
                            offerInfo: 'An offering for sales',
                            confirmation: 'Order confirmation',
                            confirmations: 'Order confirmations',
                            confirmationInfo: 'A confirmation when someone accepted your offer',
                            donation: 'Donation',
                            donations: 'Donations',
                            donationInfo: 'A donation you have or will receive',
                            invoice: 'Invoice',
                            invoices: 'Invoices',
                            invoiceInfo: 'A regular invoice for sales',
                            invoiceRefund: 'Credit note',
                            invoicesRefund: 'Credit notes',
                            invoiceRefundInfo: 'Refund an already paid invoice you made',
                            deposit: 'Deposit',
                            deposits: 'Deposits',
                            depositInfo: 'A receipt for a pre-payment you received',
                            depositRefund: 'Credit note',
                            depositsRefund: 'Credit notes',
                            depositRefundInfo: 'Refund an already paid deposit you made',
                            // -- invoice types
                            invoiceMembershipFee: 'Membership fees',
                            invoiceMembershipFeeInfo: 'An invoice made for a membership fees',
                            invoiceBooking: 'Booking invoice',
                            invoiceBookingInfo: 'An invoice made from a booking (course, event, location etc.)',
                            // -- receipt template types
                            reminder: 'Reminder',
                            returnedPayment: 'Returned payment',
                            donationReminder: 'Donation reminder',
                            // -- fake receipt types
                            invoiceCancelation: 'Cancelation',
                        },
                        typeMulti: {
                            offer: '{{count, number}} offerings',
                            offerInfo: 'Offerings for sales',
                            confirmation: '{{count, number}} order confirmations',
                            confirmationInfo: 'Confirmations when someone accepted your offer',
                            donation: '{{count, number}} donations',
                            donationInfo: 'Donations you have or will receive',
                            invoice: '{{count, number}} invoices',
                            invoiceInfo: 'Regular invoices for sales',
                            deposit: '{{count, number}} deposits',
                            depositInfo: 'Receipts for pre-payments you received',
                            // both below are never used but make TS happy
                            invoiceRefund: 'Credit note',
                            invoiceRefundInfo: 'Refund an already paid invoice you made',
                        },
                        customerType: {
                            customer: 'Customer number',
                            member: 'Member number',
                            debtor: 'Debtor number',
                            booking: 'Booking number',
                        },
                        deliveryDateType: {
                            none: 'No delivery date',
                            delivery: 'Delivery date',
                            deliveryPeriod: 'Delivery period',
                            service: 'Service date',
                            servicePeriod: 'Service period',
                        },
                        sampleTexts: {
                            accountName: 'Jon Doe',
                            details1: 'c/o Jane Doe',
                            details2: 'Apartment 4',
                            addressLine: 'Sample Street 123',
                            city: 'Sample City',
                            zip: '12345',
                            intro: 'Here comes the optional intro text of your receipt',
                            note: 'Here comes the optional note of your receipt',
                            deliveryTerms: 'Here comes your delivery terms of your receipt',
                            positionDescription: 'Position',
                            positionDetails: 'Extra position details',
                            positionUnit: 'A unit',
                            bankName: 'Sample Bank',
                            // -- reminder
                            reminder: {
                                title: 'Payment reminder',
                                intro: 'Dear Sir or Madam,\n\nIn the hustle and bustle of daily life, it is easy to overlook an invoice. Therefore, we are writing to you today. No payment has been received from you for the items listed below.',
                                note: 'Perhaps you have already paid the invoice, and the amount has simply not been credited to our account yet. In that case, everything is in order, and you can simply discard this letter. If you have indeed forgotten about the invoice, we kindly request that you transfer the amount promptly now. Thank you very much!',
                            },
                            // returnedPayment
                            returnedPayment: {
                                title: 'Returned payment',
                                intro: 'Dear Sir or Madam,\n\nUnfortunately, payment for the following items has been declined. We kindly ask you to settle the items promptly, including all fees.',
                                note: 'If there are difficulties with the payment or your payment methods, please contact us immediately to avoid the initiation of a debt collection process.',
                            },
                        },
                    },
                    receiptTextBlockType: {
                        multiple: {
                            title: 'Receipt titles',
                            intro: 'Introductory texts',
                            deliveryTerms: 'Delivery terms',
                            note: 'Footer notes',
                        },
                        single: {
                            title: 'Receipt title',
                            intro: 'Introductory text',
                            deliveryTerms: 'Delivery terms',
                            note: 'Footer notes',
                        },
                    },
                },
                reminderLevel: {
                    level1: 'Initial reminder',
                    level2: 'First reminder',
                    level3: 'Second reminder',
                    level4: 'Final reminder',
                },
                settings: {
                    vatDueMethod: {
                        receipt: 'Soll-Versteuerung (Due on invoice)',
                        payment: 'Ist-Versteuerung (Due on payment)',
                    },
                },
                paymentMethodType: {
                    sepaCreditTransfer: 'SEPA Credit Transfer',
                    sepaDirectDebit: 'SEPA Direct Debit',
                    cash: 'Cash',
                    online: 'Online',
                },
                paymentStatus: {
                    unpaid: 'Unpaid',
                    partial: 'Partial paid',
                    paid: 'Paid',
                },
                paymentDifference: {
                    up_unpaid: 'Partially paid',
                    up_discountReduction: 'Discount / Reduction',
                    up_feeNoTax: 'Banking fees without tax',
                    up_feeTax: 'Banking fees with tax',
                    op_feeNoTax: 'Banking fees without tax',
                    op_feeTax: 'Banking fees with tax',
                    op_interestFee: 'Reminder fees',
                    op_open: 'Leave difference open',
                },
                paymentMethodsSettings: {
                    sepaCreditTransfer: {
                        paymentTerms:
                            'Please transfer the amount of **[[totalDueAmount]]** until **[[dueDate]]** using the reference number **[[sepaCreditTranfer.referenceId]]** to our bank account at the bank [[sepaCreditTranfer.bankName]]:\n\nIBAN: **[[sepaCreditTranfer.iban]]**        BIC: [[sepaCreditTranfer.bic]]\n\nOr scan the Girocode on the right side with your banking app.',
                    },
                    sepaDirectDebit: {
                        paymentTerms:
                            'We will withdraw the amount due of **[[totalDueAmount]]** using the SEPA direct debit mandate **[[sepaDirectDebit.mandateId]]** to the creditor identification number [[sepaDirectDebit.creditorIdentifier]] from your account **[[sepaDirectDebit.iban]]** on **[[dueDate]]**. We ask you to ensure that your account is funded.',
                    },
                    cash: {
                        paymentTerms: 'The amount of **[[totalDueAmount]]** must be paid in cash by **[[dueDate]]**.',
                    },
                    online: {
                        paymentTerms: 'The amount of **[[totalDueAmount]]** must be paid online by **[[dueDate]]**.',
                        manualPaymentTerms:
                            'Please pay the amount of **[[totalDueAmount]]** online by **[[dueDate]]**.',
                    },
                },
            },
            channels: {
                channel: {
                    name: 'Name',
                    description: 'Description',
                },
                systemMessages: {
                    userRemoved: '{{initiatorUser}} removed {{affectedUsers}}',
                    userAdded: '{{initiatorUser}} added {{affectedUsers}}',
                    userLeft: '{{affectedUsers}} left',
                    imageChanged: '{{initiatorUser}} changed the avatar',
                    nameChanged: '{{initiatorUser}} changed the name to "{{channelName}}"',
                    descriptionChanged: '{{initiatorUser}} changed the description',
                    channelCreated: '{{initiatorUser}} created "{{channelName}}"',
                    adminPromoted: '{{initiatorUser}} gave admin rights to {{affectedUsers}}',
                    adminDemoted: '{{initiatorUser}} removed admin rights from {{affectedUsers}}',
                    joinedWithPublicLink: '{{initiatorUser}} joined using a public link',
                },
            },
            extension: {
                visibility: {
                    private: 'Private',
                    public: 'Public',
                },
                category: {
                    all: 'All categories',
                    smartAccess: 'Smart Access',
                    member: 'Member Software',
                    finances: 'Financial Software',
                    pos: 'POS Payment Systems',
                    other: 'Other',
                },
            },
            mandate: {
                allMandates: 'All mandates',
            },
            role: {
                name: 'Name',
                description: 'Description',
            },
        },
        oldEntities: {
            contactSegment: {
                defaultSegments: {
                    members: {
                        name: 'Current members',
                        hint: 'All members that have already entered and did not yet leave',
                    },
                    futureMembers: {
                        name: 'Future members',
                        hint: 'All members that will enter in the future',
                    },
                    resignedMembers: {
                        name: 'Resigned members',
                        hint: 'All members that already have left',
                    },
                    allMembers: {
                        name: 'All members',
                        hint: 'All members no matter when they entered or left ever',
                    },
                    allContacts: {
                        name: 'All contacts',
                        hint: 'All contacts without any filter',
                    },
                },
            },
            members: {
                contact: {
                    billing: {
                        billingMethod: {
                            sepaCreditTransfer: 'SEPA Credit Transfer',
                            sepaDirectDebit: 'SEPA Direct Debit',
                            cashPayment: 'Cash',
                            onlinePayment: 'Online',
                            swissQRBill: 'Swiss QR Bill',
                        },
                    },
                    personal: {
                        type: {
                            malePerson: 'Man',
                            femalePerson: 'Woman',
                            otherPerson: 'Other',
                            business: 'Business',
                            club: 'Club',
                            association: 'Association',
                            otherOrganisation: 'Organization',
                            none: 'Not specified',
                        },
                        greetings: {
                            organisation: 'Ladies and gentleman',
                            personalMale: 'Dear sir {{personLastName}}',
                            personalFemale: 'Dear mrs {{personLastName}}',
                            personalOther: 'Hi {{personFirstName}}',
                        },
                    },
                },
            },
        },
        donation: {
            donationConfirmationTextTags: {
                donatorName: {
                    name: 'Donator name',
                    description: 'The full name of the donator (first name and last name or organization name)',
                },
                donatorFirstName: {
                    name: 'Donator first name',
                    description: 'The first name of the donator, for organization contains the whole name',
                },
                donatorLastName: {
                    name: 'Donator last name',
                    description: 'The last name of the donator, for organization this is empty',
                },
                donationNumber: {
                    name: 'Donation number',
                    description: 'The number of the donation',
                },
            },
            donationConfirmationDataSchema: {
                properties: {
                    sender: {
                        name: 'Sender',
                        description: 'Complete sender details with name and address',
                    },
                    receiver: {
                        name: 'Receiver',
                        description: 'Complete receiver (donator) details with name and address',
                    },
                    donationNumber: {
                        name: 'Donation number',
                        description: 'The number of the donation',
                    },
                    content: {
                        name: 'Content',
                        description: 'The content of the donation confirmation',
                    },
                },
            },
        },
        organization: {
            contactInvitationEmail: {
                subject: 'Your access to {{organizationName}} in the Community-App',
                greeting: 'Hello,',
                singleContent:
                    'Use the button below to connect the following contact record at **{{organizationName}}** to your account:\n\n{{contactNames}}\n\nYou can then access information such as bookings, invoices, or documents linked to you through the Community-App.',
                multipleContent:
                    'Several contact records at **{{organizationName}}** use this email address:\n\n{{contactNames}}\n\nUse the button below to connect all of them to your account. You can then access information linked to you through the Community-App.',
                singleAction: 'Access with the Community-App',
                multipleAction: 'Access with the Community-App',
            },
            organizationPermissions: {
                title: 'Permissions',
                missingPermissions_one: 'Missing permission: {{permissions}}',
                missingPermissions_other: 'Missing permissions: {{permissions}}',
                categories: {
                    general: 'General',
                    crm: 'People',
                    booking: 'Events / Rentals',
                    contracts: 'Subscriptions',
                    sales: 'Sales',
                    work: 'Work',
                    donation: 'Donations',
                    finance: 'Finances',
                    accounting: 'Accounting',
                    communication: 'Communication',
                    misc: 'More',
                    portal: 'Portal',
                    connect: 'Community-App',
                    legacy: 'Legacy',
                },
                scopes: {
                    organization: 'Organization settings',
                    user: 'User management',
                    apiKey: 'API keys',
                    tag: 'Tags',
                    team: 'Teams',
                    dispatchDocument: 'Outbox',
                    notification: 'Notifications',
                    'contact:address': 'Addresses',
                    'contact:member': 'Members',
                    'contact:customer': 'Customers',
                    'contact:sponsor': 'Sponsors',
                    'contact:contributor': 'Contributors',
                    'contact:employee': 'Employees',
                    'contact:supplier': 'Suppliers',
                    contactSegment: 'Views',
                    department: 'Departments',
                    contactHistory: 'Contact history',
                    contactNote: 'Contact notes',
                    contactDocument: 'Contact documents',
                    contactGroupType: 'Group types',
                    contactGroup: 'Groups',
                    contactGroupMembership: 'Group memberships',
                    conversation: 'Inbox',
                    conversationFolder: 'Inbox folders',
                    applicationForm: 'Application forms',
                    applicationSubmission: 'Application submissions',
                    questionnaireForm: 'Forms and surveys',
                    questionnaireSubmission: 'Form and survey submissions',
                    publishedApp: 'Apps',
                    proofType: 'Required document types',
                    proof: 'Required documents',
                    certificateType: 'Certificate types',
                    certificate: 'Certificates',
                    contract: 'Subscriptions',
                    contractCollection: 'Billing runs',
                    contactRule: 'Automations',
                    rule: 'Automations',
                    plan: 'Subscription plans',
                    voucher: 'Vouchers',
                    voucherTemplate: 'Voucher templates',
                    contactPassTemplate: 'Pass templates',
                    contactIssuedPass: 'Issued passes',
                    crmSettings: 'Contact settings',
                    mailing: 'Mailings',
                    mandate: 'Mandates',
                    accountingPlan: 'Chart of accounts',
                    costCenter: 'Cost centers',
                    cashRegister: 'Cash registers',
                    compensationClaim: 'Compensation claims',
                    compensationClaimApproval: 'Compensation claim approval',
                    cashAccount: 'Cash accounts',
                    cashTransaction: 'Bank transactions',
                    cashTransactionRule: 'Bank transaction rules',
                    sepaCreditTransfer: 'Bank credit transfers',
                    sepaDirectDebit: 'Bank direct debits',
                    onlineDebitCollection: 'Online debits',
                    receipt: 'Invoices and receipts',
                    receiptTemplate: 'Receipt templates',
                    receiptTextBlock: 'Receipt text blocks',
                    posting: 'Accounting journal',
                    financesSettings: 'Financial settings',
                    debtor: 'Debtors',
                    creditor: 'Creditors',
                    fixedAsset: 'Assets',
                    product: 'Products',
                    inventory: 'Inventory',
                    salesPeriod: 'Sales periods',
                    opportunity: 'Opportunities',
                    todoItem: 'Todos',
                    todoTopic: 'Todo topics',
                    todoProject: 'Todo projects',
                    donationsSettings: 'Donation settings',
                    donation: 'Received donations',
                    austrianDonationSubmission: 'Donation submissions',
                    booking: 'Bookings',
                    bookingDiscountCode: 'Discount codes',
                    bookingCategory: 'Categories',
                    bookingView: 'Website views',
                    event: 'Events',
                    eventOffer: 'Event offer settings',
                    attendee: 'Attendees',
                    site: 'Sites',
                    resource: 'Resources',
                    resourceOffer: 'Resource offer settings',
                    resourceReservation: 'Reservations',
                    resourceCheckIn: 'Check-ins',
                    supplementaryService: 'Services',
                    punchCard: 'Punch cards',
                    portalSettings: 'Portal settings',
                    room: 'Rooms',
                    // -----------
                    // OLD CAMPAI
                    // -----------
                    file: 'Files and documents',
                    // contact
                    contact: 'Members and contacts',
                    contactReport: 'Contact reports',
                    customFieldGroup: 'Custom fields',
                    contactTag: 'Contact tags',
                    contactMembershipSection: 'Departments',
                    // messages
                    outgoingMessage: 'Sent messages',
                    messageTemplate: 'Message templates',
                    // forms
                    form: 'Forms and applications',
                    formSubmission: 'Form submissions',
                    // contributions
                    contributionRate: 'Contribution types',
                    contribution: 'Contributions',
                    billing: 'Finance and contribution settings',
                    billingLabel: 'Invoice labels',
                    invoice: 'Invoices and donations',
                    cashAccountOrderStatus: 'Payment orders',
                    // accounting
                    accounting: 'Accounting',
                    bookEntry: 'Accounting journal and reports',
                    // passes
                    walletPass: 'Digital passes',
                    walletPassTemplate: 'Pass templates',
                },
                scopePermissions: {
                    read: 'Read',
                    insert: 'Insert',
                    edit: 'Edit',
                    remove: 'Remove',
                    restricted: 'Restricted',
                },
            },
            restrictions: {
                title: 'Restrictions',
                scopes: {
                    mandate: 'Mandate',
                },
            },
            subscriptionStatus: {
                trial: 'Trial',
                active: 'Active',
                inactive: 'Inactive',
                willCancel: 'Will cancel',
                canceled: 'Canceled',
                trialEnded: 'Trial ended',
            },
            newCRMMode: {
                notAvailable: 'Not available',
                available: 'Available',
                replace: 'Replace',
            },
            migrationStatus: {
                available: 'Available',
                inProgress: 'In progress',
                finished: 'Finished',
            },
            classification: {
                type: {
                    club: 'Club',
                    association: 'Association',
                    foundation: 'Foundation',
                    charity: 'Charity',
                    church: 'Church',
                    political: 'Political',
                    cooperative: 'Cooperativa',
                    gGmbH: 'gGmbH',
                    other: 'Other',
                },
                area: {
                    culture: 'Culture',
                    political: 'Political',
                    business: 'Business',
                    environment: 'Environment',
                    human: 'Human',
                    relax: 'Relax',
                    sports: 'Sports',
                    other: 'Other',
                },
                sports: {
                    airSports: 'Air sports',
                    rockClimbing: 'Rock climbing',
                    americanFootball: 'American football',
                    cheerleading: 'Cheerleading',
                    badminton: 'Badminton',
                    baseball: 'Baseball',
                    softball: 'Softball',
                    basketball: 'Basketball',
                    disabilitySports: 'Sports for people with disabilities',
                    billiards: 'Billiards',
                    bobsleigh: 'Bobsleigh',
                    tobogganing: 'Tobogganing',
                    skeleton: 'Skeleton',
                    boccia: 'Boccia',
                    boule: 'Boule',
                    petanque: 'Petanque',
                    boxing: 'Boxing',
                    cricket: 'Cricket',
                    curling: 'Curling',
                    darts: 'Darts',
                    iceHockey: 'Ice hockey',
                    figureSkating: 'Figure skating',
                    iceDancing: 'Ice dancing',
                    speedSkating: 'Speed skating',
                    shortTrack: 'Short track',
                    stickShooting: 'Stick shooting',
                    fencing: 'Fencing',
                    floorball: 'Floorball',
                    football: 'Football',
                    deafSports: 'Sports for deaf people',
                    weightLifting: 'Weight lifting',
                    golf: 'Golf',
                    handball: 'Handball',
                    hockey: 'Hockey',
                    judo: 'Judo',
                    jujutsu: 'Jujutsu',
                    canoeing: 'Canoeing',
                    karate: 'Karate',
                    bowling: 'Bowling',
                    kickboxing: 'Kickboxing',
                    powerLifting: 'Power lifting',
                    rescueSports: 'Rescue sports',
                    athletics: 'Athletics',
                    miniGolf: 'Mini golf',
                    motorsport: 'Motorsport',
                    motorboating: 'Motorboating',
                    cycling: 'Cycling',
                    lawnPowerSports: 'Lawn power sports',
                    tugOfWar: 'Tug of war',
                    equestrianSports: 'Equestrian sports',
                    wrestling: 'Wrestling',
                    roller: 'Roller',
                    inlineSkating: 'Inline skating',
                    rowing: 'Rowing',
                    rugby: 'Rugby',
                    chess: 'Chess',
                    shooting: 'Shooting',
                    swimming: 'Swimming',
                    sailing: 'Sailing',
                    skibob: 'Skibob',
                    ski: 'Ski',
                    acrobatics: 'Acrobatics',
                    scuba: 'Scuba',
                    squash: 'Squash',
                    taekwondo: 'Taekwondo',
                    dancing: 'Dancing',
                    tennis: 'Tennis',
                    tableTennis: 'Table tennis',
                    triathlon: 'Triathlon',
                    gymnastics: 'Gymnastics',
                    snowboard: 'Snowboard',
                    pentathlon: 'Pentathlon',
                    volleyball: 'Volleyball',
                    waterSki: 'Water Ski',
                    wakeboard: 'Wakeboard',
                    surfing: 'Surfing',
                    martialArts: 'Martial arts',
                    kungFu: 'Kung Fu',
                    fistball: 'Fistball',
                    eSports: 'E-Sports',
                    aikido: 'Aikido',
                    multispeciality: 'Multi Speciality Sports',
                    archery: 'Archery',
                    standUpPaddle: 'Stand-up paddle',
                    taiChi: 'Tai-chi',
                    qiGong: 'Qi-gong',
                    sanda: 'Sanda',
                    selfDefense: 'Self-defense',
                    fitness: 'Fitness sports',
                    childrenSport: 'Children sports',
                    running: 'Running/Endurance',
                    ropeSkipping: 'Rope skipping',
                    shuttlecock: 'Shuttlecock',
                    fingerBoarding: 'Fingerboarding',
                    flagFootball: 'Flag Football',
                    flightModel: 'Flight Model Sport',
                    frisbee: 'Frisbee',
                    climbing: 'Climbing',
                    walking: 'Walking',
                    geocaching: 'Geocaching',
                    paragliding: 'Paragliding',
                    jiuJutsu: 'Jiu-Jutsu',
                    kendo: 'Kendo',
                    kravMaga: 'Krav Maga',
                    lacrosse: 'Lacrosse',
                    mountainBiking: 'Mountainbiking',
                    muayThai: 'Muay Thai',
                    nordicWalking: 'Nordic walking',
                    orienteeringRace: 'Orienteering race',
                    paintBall: 'Paintball',
                    parkour: 'Parkour',
                    poker: 'Poker',
                    polo: 'Polo',
                    poleDance: 'Pole Dance',
                    prellball: 'Prellball',
                    racketlon: 'Racketlon',
                    waterSport: 'Watersport',
                    skateboarding: 'Skateboarding',
                    stockschiessen: 'Stockschiessen',
                    wingTsun: 'Wing Tsun',
                    wushu: 'Wushu',
                    circus: 'Circus',
                    popularSport: 'Popular sports',
                    other: 'Other sports',
                    skiing: 'Skiing',
                    snowboarding: 'Snowboarding',
                    crossCountrySkiing: 'Cross country skiing',
                },
            },
        },
        booking: {
            getBookingRateStartPriceString: {
                free: 'Free',
                startingAt: 'From {{price}}',
            },
        },
        crm: {
            contract: {
                createContractPeriodDatesProperties: {
                    groupName: 'Period dates',
                    periodStart: 'Period start',
                    periodEnd: 'Period end',
                    intervalStart: 'Interval start',
                    intervalEnd: 'Interval end',
                },
                contractTextTags: {
                    intervalStart: 'Interval start',
                    intervalEnd: 'Interval end',
                    periodStart: 'Period start',
                    periodEnd: 'Period end',
                    collection: 'Collection',
                    receiptFinal: 'Receipt',
                    collectAt: 'Collection date',
                    dueAt: 'Due date',
                    amount: 'Amount',
                    totalAmountToPay: 'Total amount to pay',
                    onlinePaymentLink: 'Online payment link',
                    contributions: 'Contracts',
                    debtorReceiptSendMethod: 'Send method',
                    debtorPaymentMethodType: 'Payment method',
                    debtorSepaAccountHolderName: 'SEPA account holder',
                    dateTags: {
                        date: 'Formatted date',
                        day: 'Day of month',
                        month: 'Month number',
                        monthName: 'Month name',
                        monthNameShort: 'Month name short',
                        quarter: 'Quarter number',
                        quarterName: 'Quarter name',
                        halfYear: 'Half year number',
                        halfYearName: 'Half year name',
                        year: 'Year number',
                    },
                },
            },
            contact: {
                getContactGreetings: {
                    male: 'Dear Mr. {{lastName}}',
                    female: 'Dear Ms. {{lastName}}',
                    diverse: 'Dear Mr./Ms. {{lastName}}',
                    neutral: 'Hello {{firstName}} {{lastName}}',
                    institution: 'Dear Ladies and Gentlemen',
                },
            },
            pass: {
                getDefaultEmailTexts: {
                    subject: 'Your digital pass',
                    message: {
                        greetings: 'Hello [[Name]],',
                        intro: 'Your new digital pass is available.',
                        downloadLink:
                            'To download the card, click on the following link, depending on which mobile phone you are using:',
                    },
                },
                createContactPassExpressaSchema: {
                    internalGroupName: 'Internal',
                    recognizableBarcodeDataName: 'Recognizable Barcode Data',
                    validFromName: 'Valid from',
                    validToName: 'Valid to',
                },
            },
            mailing: {
                getMailingSchemaAndData: {
                    mailing: {
                        groupName: 'Mailing',
                        unsubscribeLink: {
                            name: 'Unsubscribe link',
                        },
                    },
                },
            },
            communication: {
                getLetterProprietiesFromContact: {
                    letterAddress: {
                        groupName: 'Letter full Address',
                        sender: {
                            name: 'Sender address',
                        },
                        receiver: {
                            name: 'Receiver address',
                        },
                    },
                },
            },
        },
        finances: {
            accounting: {
                formatAccountBalance: {
                    balanceType: {
                        debit: 'D',
                        credit: 'C',
                    },
                },
            },
            receipts: {
                receiptDataSchema: {
                    shared: {
                        properties: {
                            sender: {
                                name: 'Sender',
                                description: 'Complete sender details with name and address',
                            },
                            receiver: {
                                name: 'Receiver',
                                description: 'Complete receiver details with name and address',
                            },
                            customerType: {
                                name: 'Customer type',
                                description: 'The type of the customer ie member or customer',
                            },
                            customerNumber: {
                                name: 'Customer number',
                                description: 'The customer / member / debtor number, can be empty',
                            },
                            paymentTerms: {
                                name: 'Payment Terms',
                                description: 'The payment terms of the receipt, can be empty',
                            },
                            epcQRData: {
                                name: 'EPC-QR Code',
                                description: 'The data for an epc-qr code when credit transfer, can be empty',
                            },
                        },
                    },
                    receipt: {
                        properties: {
                            title: {
                                name: 'Title',
                                description: 'The title of the receipt',
                            },
                            intro: {
                                name: 'Intro',
                                description: 'The intro text of the receipt, can be empty',
                            },
                            deliveryTerms: {
                                name: 'Delivery Terms',
                                description: 'The delivery terms of the receipt, can be empty',
                            },
                            note: {
                                name: 'Note',
                                description: 'The final notes of the receipt, can be empty',
                            },
                            receiptDate: {
                                name: 'Receipt date',
                                description: 'The date of the receipt',
                            },
                            receiptNumber: {
                                name: 'Receipt number',
                                description: 'The receipt number',
                            },
                            dueDate: {
                                name: 'Due date',
                                description: 'The due date of the receipt, can be empty',
                            },
                            deliveryDateType: {
                                name: 'Delivery date type',
                                description: 'The delivery date type of the receipt, can be empty',
                            },
                            deliveryDate: {
                                name: 'Delivery date',
                                description: 'The delivery date of the receipt, can be empty',
                            },
                            positions: {
                                name: 'Positions',
                                description: 'On or more positions of the receipt',
                                properties: {
                                    position: {
                                        name: 'Position',
                                        description: 'The position number',
                                    },
                                    description: {
                                        name: 'Description',
                                        description: 'The product / description of the position',
                                    },
                                    details: {
                                        name: 'Details',
                                        description: 'Optional details of the positions, can be empty',
                                    },
                                    quantity: {
                                        name: 'Quantity',
                                        description: 'The quantity of the position, can be empty',
                                    },
                                    unit: {
                                        name: 'Unit',
                                        description: 'The unit of the position, can be empty',
                                    },
                                    unitAmount: {
                                        name: 'Unit price',
                                        description: 'The price per unit of the position, can be empty',
                                    },
                                    total: {
                                        name: 'Total',
                                        description: 'The total price of the position',
                                    },
                                    tax: {
                                        name: 'Tax',
                                        description: 'The tax of the position, can be empty',
                                    },
                                    discount: {
                                        name: 'Discount',
                                        description: 'The discount of the position, can be empty',
                                    },
                                },
                            },
                            totals: {
                                name: 'Totals',
                                description: 'A list of sums, taxes, discounts and used credits',
                                properties: {
                                    type: {
                                        name: 'Type',
                                        description: 'The rows type one of subTotal | total | tax | discount',
                                    },
                                    label: {
                                        name: 'Label',
                                        description: 'The rows actual label',
                                    },
                                    value: {
                                        name: 'Value',
                                        description: 'The rows actual value',
                                    },
                                },
                                labels: {
                                    includedTaxes: 'Included taxes',
                                    discount: 'Discount',
                                    depositUsed: 'Already paid',
                                    subTotalNet: 'Net subtotal',
                                    subTotalGross: 'Subtotal',
                                    totalNetAmount: 'Total net',
                                    totalGrossAmount: 'Total amount',
                                },
                            },
                        },
                    },
                    reminder: {
                        payment: 'Payment',
                        levels: {
                            level1: {
                                fee: 'Initial reminder fee',
                            },
                            level2: {
                                fee: 'First reminder fee',
                            },
                            level3: {
                                fee: 'Second reminder fee',
                            },
                            level4: {
                                fee: 'Final reminder fee',
                            },
                        },
                        properties: {
                            title: {
                                name: 'Title',
                                description: 'The title of the reminder',
                            },
                            intro: {
                                name: 'Intro',
                                description: 'The intro of the reminder, can be empty',
                            },
                            note: {
                                name: 'Note',
                                description: 'The note of the reminder, can be empty',
                            },
                            unpaidAmount: {
                                name: 'Unpaid amount',
                                description: 'The unpaid amount at that time including this and any previous fees',
                            },
                            fee: {
                                name: 'Fee amount',
                                description: 'The fee for this specific reminder',
                            },
                            date: {
                                name: 'Date',
                                description: 'The date of the reminder',
                            },
                            receiptTitle: {
                                name: 'Receipt title',
                                description: 'The title of the receipt',
                            },
                            receiptDate: {
                                name: 'Receipt date',
                                description: 'The date of the receipt',
                            },
                            receiptNumber: {
                                name: 'Receipt number',
                                description: 'The receipt number',
                            },
                            receiptDueDate: {
                                name: 'Receipt due date',
                                description: 'The actual receipt`s passed due date',
                            },
                            receiptGrossAmount: {
                                name: 'Receipt gross amount',
                                description: 'The total gross amount of the original receipt',
                            },
                            receiptFees: {
                                name: 'Receipt fees',
                                description: 'The total fees of the original receipt',
                            },
                            assessments: {
                                name: 'Assessments',
                                description: 'A list of receipt assessments: reminder levels or payments',
                            },
                        },
                    },
                    returnedPayment: {
                        properties: {
                            title: {
                                name: 'Title',
                                description: 'The title of the reminder',
                            },
                            intro: {
                                name: 'Intro',
                                description: 'The intro of the returned payment reminder, can be empty',
                            },
                            note: {
                                name: 'Note',
                                description: 'The note of the returned payment reminder, can be empty',
                            },
                            unpaidAmount: {
                                name: 'Unpaid amount',
                                description: 'The unpaid amount at that time including any fees',
                            },
                            returnFee: {
                                name: 'Return fee amount',
                                description: 'The fee of the bank for the returned payment',
                            },
                            processingFee: {
                                name: 'Processing fee amount',
                                description: 'Additional interest fees from your side',
                            },
                            date: {
                                name: 'Date',
                                description: 'The date of the returned payment reminder',
                            },
                            receiptTitle: {
                                name: 'Receipt title',
                                description: 'The title of the receipt',
                            },
                            receiptDate: {
                                name: 'Receipt date',
                                description: 'The date of the receipt',
                            },
                            receiptNumber: {
                                name: 'Receipt number',
                                description: 'The receipt number',
                            },
                            receiptDueDate: {
                                name: 'Receipt due date',
                                description: 'The actual receipt`s passed due date',
                            },
                            receiptGrossAmount: {
                                name: 'Receipt gross amount',
                                description: 'The total gross amount of the original receipt',
                            },
                        },
                    },
                },
                receiptTextTags: {
                    accountName: {
                        name: 'Account name',
                        description: 'The full name of the recipient (first name and last name or organization name)',
                    },
                    accountFirstName: {
                        name: 'Account first name',
                        description: 'The first name of the recipient, for organization contains the whole name',
                    },
                    accountLastName: {
                        name: 'Account last name',
                        description: 'The last name of the recipient, for organization this is empty',
                    },
                    customerNumber: {
                        name: 'Customer number',
                        description: 'The customer number of the recipient',
                    },
                    totalDueAmount: {
                        name: 'Due amount',
                        description: 'The amount still to be paid including any possible fees',
                    },
                    totalFeesAmount: {
                        name: 'Fees amount',
                        description: 'The total amount of all fees',
                    },
                    totalGrossAmount: {
                        name: 'Total amount',
                        description: 'The total amount of the receipt',
                    },
                    totalPaidAmount: {
                        name: 'Paid amount',
                        description: 'The total paid amount',
                    },
                    dueDate: {
                        name: 'Due at',
                        description: 'Date the receipt is due',
                    },
                    receiptNumber: {
                        name: 'Receipt number',
                        description: 'The receipts actual number',
                    },
                    sepaCreditTranfer: {
                        iban: {
                            name: 'Bank IBAN',
                            description: 'Your bank account`s IBAN to transfer to',
                        },
                        bic: {
                            name: 'Bank BIC',
                            description: 'Your bank`s BIC to transfer to',
                        },
                        bankName: {
                            name: 'Bank name',
                            description: 'Your bank`s name to transfer to',
                        },
                        referenceId: {
                            name: 'Reference number',
                            description: 'The reference number to be used to identity the transfer',
                        },
                    },
                    sepaDirectDebit: {
                        creditorIdentifier: {
                            name: 'Creditor Identifier',
                            description: 'Your creditor identifier for the direct debit',
                        },
                        iban: {
                            name: 'Payer IBAN',
                            description: 'The payer`s IBAN the direct debit is executed on',
                        },
                        mandateId: {
                            name: 'Mandate reference',
                            description: 'The payer`s mandate id the direct debit is executed with',
                        },
                    },
                },
                receiptPaymentTerms: {
                    paymentDetails:
                        'Payment by {{method}} received on {{date}} – Thank You! No further action is required.',
                    paymentType: {
                        deposit: 'Deposit',
                    },
                },
            },
            emails: {
                defaultEmail: {
                    name: 'Content',
                    description: 'Description',
                },
            },
        },
        portal: {
            channel: {
                allMention: 'all',
            },
            email: {
                missingLinkPlaceholder: 'The link is missing on the email template',
                missingTokenPlaceholder: 'The invitation token is missing on the email template',
                getPortalEmailSchema: {
                    recipientName: {
                        name: 'Recipient name',
                        description: 'The name of the recipient',
                    },
                    organizationName: {
                        name: 'Organization name',
                        description: 'The name of the organization',
                    },
                    invitationLink: {
                        name: 'Invitation link',
                        description: 'The link to the invitation',
                    },
                    invitationToken: {
                        name: 'Invitation token',
                        description: 'The token to the invitation',
                    },
                },
            },
        },
        simpleForm: {
            transformValue: {
                transformContext: 'transforming value with "{{transform}}"',
            },
            evaluateSimpleForm: {
                fieldNotFound: 'No field with id "{{fieldId}}" found',
            },
            validation: {
                invalidValue: 'Invalid value',
            },
        },
        misc: {
            default: 'Default',
            total: 'Total',
            unknown: 'Unknown',
            notSet: 'Not set',
            close: 'Close',
            fullYear: 'Ganzes Jahr',
            today: 'Today',
            yesterday: 'Yesterday',
            tomorrow: 'Tomorrow',
            untitled: 'Untitled',
            and: 'and',
            or: 'or',
            yes: 'Yes',
            no: 'No',
            numberSeparatorWord: 'Dot',
            distanceUnitShort: {
                meter: 'm',
                kilometer: 'km',
                mile: 'mi',
            },
            dateTimeFromTo: '{{date}} from {{from}} to {{to}} o`clock',
            dateFromTo: '{{from}} to {{to}}',
            dateFrom: 'From',
            dateTo: 'To',
            point: 'Point',
            pointShort: 'Pt',
            points: 'Points',
            pointsShort: 'Pts',
            freePrice: 'Free',
            unit: 'Unit',
            units: 'Units',
            copyOf: 'Copy of {{name}}',
            item: 'Item',
            items: 'Items',
            formatSequenceNumber: {
                duplicateCounterPlaceholder:
                    'Another counter placeholders found at position {{position}}, but only one allowed',
                invalidCounterPlaceholder: 'Invalid counter placeholder at position {{position}}, expected digit 1-9',
                missingCounterPlaceholder: 'Missing a placeholder for the counter',
                counterTypeNotAllowed: 'Selected counter placeholder is not allowed',
            },
            selectionCount: '{{count}} selected',
        },
    },
}
